import { union, intersect, difference, length, area, circle, unkinkPolygon } from "@turf/turf";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { polygon, point, Feature, LineString, Polygon } from "@turf/helpers";
import L, { LatLng, LeafletMouseEvent } from "leaflet";
import { useCallback, useEffect, useRef } from "react";
import { GAIA_DRAW_MODE, GaiaDrawModeType } from "../GaiaMap";
import { MapHistoryState, useGaiaMapContext } from "./useGaiaMapContext";
import { GetBoundaryRequest } from "@shared/mirage/core/contracts/GetBoundaryRequest";
import { GeometryProjector } from "@shared/mirage/utils/GeometryProjector";
import useAlertService from "@mirage/hooks/useAlertService";
import { useConfirmDialog } from "@mirage/hooks/useConfirmDialog";
import Field from "@mirage/components/regularField/Field";
import { MapViewHelper } from "@mirage/components/MapView/MapViewHelper";
import { createAwesomeIcon } from "@mirage/components/leaflet/AwesomeMarker";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { STATMAP_PREFIX } from "@shared/mirage/services/WmsCapabilities";

function symDiff(a: any, b: any): any {
    try {
        const aMinusB = difference(a as any, b as any);
        const bMinusA = difference(b as any, a as any);
        if (aMinusB && bMinusA) return union(aMinusB as any, bMinusA as any);
        return aMinusB || bMinusA || null;
    } catch {
        try {
            return union(a as any, b as any);
        } catch {
            return a;
        }
    }
}

interface InitializeDrawingProps {
    map: L.DrawMap | null;
    drawModeRef: React.MutableRefObject<GaiaDrawModeType>;
    points: React.MutableRefObject<LatLng[]>;
    featureGroup: React.MutableRefObject<L.FeatureGroup>;
    tempCircle: React.MutableRefObject<L.Polygon | null>;
}

interface RulerLine {
    id: string;
    points: L.LatLng[];
    line: L.Polyline;
    markers: L.CircleMarker[];
    tooltip: L.Tooltip;
    segments: L.Polyline[];
}

export const useInitializeDrawing = (props: InitializeDrawingProps) => {
    const { map, drawModeRef, points, featureGroup, tempCircle } = props;
    const circleMarkers = useRef<L.CircleMarker[]>([]);
    const firstPointCircle = useRef<L.CircleMarker | null>(null);
    const currentLayerRef = useRef<L.Polyline | L.Polygon | null>(null);
    const tempLine = useRef<L.Polyline | null>(null);
    const circleCenter = useRef<L.LatLng | null>(null);
    const showAlert = useAlertService();
    const circleCenterMarker = useRef<L.Marker | null>(null);
    const [confirm] = useConfirmDialog();
    const circleTooltip = useRef<L.Tooltip | null>(null);
    const {
        featureGroupToWorkingGeometry,
        pushHistory,
        getUndoState,
        getRedoState,
        setDrawMode,
        onClone,
        layerOnClick,
        updateWorkingGeometry,
        workingGeometry,
        onChange,
        onSelect,
        getFeatureInfo,
        wmsLayers,
        wmsUrl,
        pointCustomIcon,
        singleGeometryMode,
        drawMode,
        pointAsCircleMarker,
        suppressNextFit,
    } = useGaiaMapContext();
    const isReadonlyLayer = useCallback((layer: any) => {
        if (!layer) return false;
        return (layer as any)._gaiaRole === "readonly" || (layer as any)._readonly === true || (layer.options?.className || "").toString().includes("readonly-layer");
    }, []);
    const distanceTooltip = useRef<L.Tooltip | null>(null);
    const rulerLine = useRef<L.Polyline | null>(null);
    const rulerPoints = useRef<L.LatLng[]>([]);
    const rulerLines = useRef<RulerLine[]>([]);
    const activeRulerLine = useRef<RulerLine | null>(null);
    const rulerLineSegments = useRef<L.Polyline[]>([]);
    const firstRulerPointMarker = useRef<L.CircleMarker | null>(null);
    const currentInspectionMarker = useRef<L.Marker | null>(null);
    const editVertexMarkers = useRef<L.CircleMarker[]>([]);
    const editMidpointMarkers = useRef<L.CircleMarker[]>([]);
    const editingLayer = useRef<L.Polygon | L.Polyline | null>(null);
    const draggedVertexIndex = useRef<number | null>(null);
    const editMarkersByLayer = useRef<Map<L.Polygon | L.Polyline, { vertex: L.CircleMarker[]; mid: L.CircleMarker[] }>>(new Map());

    const calculateArea = useCallback((points: L.LatLng[]) => {
        if (points.length < 3) {
            return 0;
        }

        const closedPoints = points[0].equals(points[points.length - 1]) ? points : [...points, points[0]];

        const coords = closedPoints.map((p) => [p.lng, p.lat]);

        const polygonFeature: Feature<Polygon> = {
            type: "Feature",
            properties: {},
            geometry: {
                type: "Polygon",
                coordinates: [coords],
            },
        };

        const areaInSquareMeters = area(polygonFeature);
        return areaInSquareMeters / 1000000;
    }, []);

    const calculateDistance = useCallback((points: L.LatLng[]) => {
        if (points.length < 2) {
            return 0;
        }

        const coords = points.map((p) => [p.lng, p.lat]);
        const line: Feature<LineString> = {
            type: "Feature",
            properties: {},
            geometry: {
                type: "LineString",
                coordinates: coords,
            },
        };

        const distanceInKm = length(line);
        return distanceInKm;
    }, []);

    const updateRulerTooltip = useCallback(
        (points: L.LatLng[], mousePos?: L.LatLng) => {
            if (!map || points.length === 0) {
                return;
            }

            const pointsToMeasure = mousePos ? [...points, mousePos] : points;
            const distance = calculateDistance(pointsToMeasure);
            const formattedDistance = distance < 1 ? `${(distance * 1000).toFixed(1)} m` : `${distance.toFixed(2)} km`;

            const tooltipPos = mousePos || points[points.length - 1];

            if (distanceTooltip.current) {
                map.removeLayer(distanceTooltip.current);
            }

            distanceTooltip.current = L.tooltip({
                permanent: true,
                direction: "right",
                className: "ruler-tooltip",
            })
                .setLatLng(tooltipPos)
                .setContent(formattedDistance)
                .addTo(map);
        },
        [map, calculateDistance]
    );

    const finishRulerLine = useCallback(() => {
        if (!map || rulerPoints.current.length < 2) {
            return;
        }

        const line = L.polyline(rulerPoints.current, {
            ...MapViewHelper.getLayerStyling("ruler-line"),
            interactive: true,
            className: "ruler-line",
        } as any);

        const rulerLineId = Date.now().toString();
        const distance = calculateDistance(rulerPoints.current);
        const formattedDistance = distance < 1 ? `${(distance * 1000).toFixed(1)} m` : `${distance.toFixed(2)} km`;

        const tooltipContent = document.createElement("div");
        tooltipContent.style.display = "flex";
        tooltipContent.style.alignItems = "center";
        tooltipContent.style.gap = "8px";
        tooltipContent.className = "ruler-tooltip-content";

        const distanceText = document.createElement("span");
        distanceText.textContent = formattedDistance;
        tooltipContent.appendChild(distanceText);

        const deleteButton = document.createElement("button");
        deleteButton.className = "ruler-delete-button";
        deleteButton.textContent = "❌";
        deleteButton.style.background = "none";
        deleteButton.style.border = "none";
        deleteButton.style.padding = "0";
        deleteButton.style.cursor = "pointer";
        deleteButton.style.fontSize = "12px";
        deleteButton.style.lineHeight = "1";
        deleteButton.title = "Delete measurement";

        tooltipContent.appendChild(deleteButton);

        const tooltip = L.tooltip({
            permanent: true,
            direction: "right",
            className: "ruler-tooltip",
            offset: [10, 0],
            interactive: true,
        })
            .setLatLng(rulerPoints.current[rulerPoints.current.length - 1])
            .setContent(tooltipContent);

        const measurement = L.featureGroup();
        measurement.addLayer(line);
        measurement.addLayer(tooltip);
        circleMarkers.current.forEach((marker) => {
            measurement.addLayer(marker);
        });

        map.addLayer(measurement);
        const segments = [...rulerLineSegments.current];
        rulerLineSegments.current = [];

        const newRulerLine: RulerLine = {
            id: rulerLineId,
            points: [...rulerPoints.current],
            line,
            markers: circleMarkers.current,
            tooltip,
            segments,
        };

        function removeRulerLine() {
            if (map && map.hasLayer(measurement)) {
                map.removeLayer(measurement);
            }

            newRulerLine.segments.forEach((segment) => {
                if (map && map.hasLayer(segment)) {
                    map.removeLayer(segment);
                }
            });

            rulerLines.current = rulerLines.current.filter((rl) => rl.id !== rulerLineId);
        }

        deleteButton.addEventListener("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
            removeRulerLine();
        });

        deleteButton.addEventListener("mousedown", (e) => {
            e.stopPropagation();
            e.preventDefault();
        });

        rulerLines.current.push(newRulerLine);
        rulerPoints.current = [];
        firstRulerPointMarker.current = null;

        if (rulerLine.current) {
            map.removeLayer(rulerLine.current);
            rulerLine.current = null;
        }
        if (distanceTooltip.current) {
            map.removeLayer(distanceTooltip.current);
            distanceTooltip.current = null;
        }
        circleMarkers.current.forEach((marker) => map.removeLayer(marker));
        circleMarkers.current = [];
    }, [map, calculateDistance]);

    const clearAllDrawingElements = useCallback(() => {
        if (!map) {
            return;
        }

        map.eachLayer((layer) => {
            if (layer instanceof L.Polyline) {
                // Only remove temporary drawing helpers, never real geometries.
                // Do NOT key off dashArray, as real features can be dashed via palette.
                const isTempDrawingClass = !!(layer.options.className && layer.options.className.includes("temp-drawing"));
                const isTempLineRef = layer === tempLine.current;
                const isDetachedCurrentDraft = layer === currentLayerRef.current && !featureGroup.current.hasLayer(layer);
                if (!isReadonlyLayer(layer) && (isTempDrawingClass || isTempLineRef || isDetachedCurrentDraft) && !(layer as any)._initialBoundary) {
                    map.removeLayer(layer);
                }
            }

            if (layer instanceof L.CircleMarker) {
                // Remove only temporary editing markers. Keep feature points.
                const isEditing = (layer as any)._gaiaRole === "editing";
                const notInFeatureGroup = !featureGroup.current.hasLayer(layer) && !(layer as any)._initialBoundary;
                if ((isEditing || notInFeatureGroup) && !isReadonlyLayer(layer)) {
                    map.removeLayer(layer);
                }
            }
        });

        if (tempLine.current) {
            map.removeLayer(tempLine.current);
            tempLine.current = null;
        }

        if (currentLayerRef.current && !featureGroup.current.hasLayer(currentLayerRef.current) && !(currentLayerRef.current as any)._initialBoundary) {
            map.removeLayer(currentLayerRef.current);
            currentLayerRef.current = null;
        }

        circleMarkers.current.forEach((marker) => {
            if (marker && map.hasLayer(marker)) {
                map.removeLayer(marker);
            }
        });
        circleMarkers.current = [];

        if (firstPointCircle.current) {
            map.removeLayer(firstPointCircle.current);
            firstPointCircle.current = null;
        }

        if (circleTooltip.current) {
            map.removeLayer(circleTooltip.current);
            circleTooltip.current = null;
        }

        points.current = [];

        if (distanceTooltip.current) {
            map.removeLayer(distanceTooltip.current);
            distanceTooltip.current = null;
        }

        if (rulerLine.current) {
            map.removeLayer(rulerLine.current);
            rulerLine.current = null;
        }

        if (drawModeRef.current !== GAIA_DRAW_MODE.RULER) {
            rulerPoints.current = [];
            if (activeRulerLine.current) {
                activeRulerLine.current = null;
            }
            if (rulerLine.current) {
                map.removeLayer(rulerLine.current);
                rulerLine.current = null;
            }
            if (distanceTooltip.current) {
                map.removeLayer(distanceTooltip.current);
                distanceTooltip.current = null;
            }
        }
    }, [map, featureGroup, points, drawModeRef]);

    const clearDrawingState = useCallback(() => {
        if (!map) {
            return;
        }
        clearAllDrawingElements();
    }, [map, clearAllDrawingElements]);

    const saveCurrentState = useCallback(
        (isDrawing = false) => {
            const isComplete = !isDrawing && drawModeRef.current === GAIA_DRAW_MODE.POLYGON && featureGroup.current.getLayers().some((layer) => layer instanceof L.Polygon);

            pushHistory(
                {
                    points: [...points.current],
                    featureGroup: featureGroup.current,
                    drawMode: drawModeRef.current,
                    isComplete: isComplete,
                    isDrawing: isDrawing,
                },
                isDrawing
            );
        },
        [pushHistory, points, featureGroup, drawModeRef]
    );

    const clearCurrentState = useCallback(() => {
        if (!map) {
            return;
        }
        clearDrawingState();
    }, [map, clearDrawingState]);

    const getCoordsFromLatLngs = useCallback((latLngs: L.LatLng[] | L.LatLng[][] | L.LatLng[][][]): [number, number][][] => {
        if (!latLngs || !latLngs.length) {
            return [];
        }

        if (Array.isArray(latLngs[0]) && latLngs[0][0] instanceof L.LatLng) {
            return (latLngs as L.LatLng[][]).map((ring) => ring.map((ll) => [ll.lng, ll.lat]));
        } else if (latLngs[0] instanceof L.LatLng) {
            return [(latLngs as L.LatLng[]).map((ll) => [ll.lng, ll.lat])];
        } else if (Array.isArray(latLngs[0]) && Array.isArray(latLngs[0][0]) && latLngs[0][0][0] instanceof L.LatLng) {
            return (latLngs[0] as L.LatLng[][]).map((ring) => ring.map((ll) => [ll.lng, ll.lat]));
        }

        return [];
    }, []);

    const removePointsInsideProjectedGeometry = useCallback(
        (geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon) => {
            const layersToRemove = new Set<L.Layer>();
            featureGroup.current.eachLayer((layer) => {
                if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
                    const latLng = layer.getLatLng();
                    const pointFc: GeoJSON.FeatureCollection = {
                        type: "FeatureCollection",
                        features: [
                            {
                                type: "Feature",
                                properties: {},
                                geometry: { type: "Point", coordinates: [latLng.lng, latLng.lat] } as GeoJSON.Point,
                            },
                        ],
                    };

                    try {
                        const projected = GeometryProjector.projectTo27700(pointFc);
                        const projectedPoint = projected.features[0].geometry as GeoJSON.Point;
                        if (booleanPointInPolygon(projectedPoint as any, geometry as any)) {
                            layersToRemove.add(layer);
                        }
                    } catch (error) {
                        console.error("Error checking point containment in polygon:", error);
                    }
                }
            });

            layersToRemove.forEach((layer) => {
                featureGroup.current.removeLayer(layer);
                if (map && map.hasLayer(layer)) {
                    map.removeLayer(layer);
                }
            });
        },
        [featureGroup, map]
    );

    const unionPolygons = useCallback(
        (newLayer: L.Polygon): L.Polygon | null => {
            if (!map) {
                return null;
            }
            const mapInstance = map;
            const newLatLngs = newLayer.getLatLngs();
            const newCoordsArrays = getCoordsFromLatLngs(newLatLngs);

            if (!newCoordsArrays.length || newCoordsArrays[0].length < 3) {
                console.error("Cannot create polygon with less than 3 points");
                return null;
            }

            const newCoords = newCoordsArrays;
            for (const ring of newCoords) {
                const firstPoint = ring[0];
                const lastPoint = ring[ring.length - 1];
                if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
                    ring.push([...firstPoint]);
                }
            }

            let merged: any;
            try {
                const newFcWgs: GeoJSON.FeatureCollection = {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            properties: {},
                            geometry: { type: "Polygon", coordinates: newCoords } as any,
                        },
                    ],
                };
                const newFc27700 = GeometryProjector.projectTo27700(newFcWgs);
                const projectedNewCoords = (newFc27700.features[0].geometry as GeoJSON.Polygon).coordinates as any;
                merged = polygon(projectedNewCoords);
            } catch (error) {
                console.error("Error creating polygon:", error);
                return null;
            }

            const overlapping: L.Polygon[] = [];
            const layersToRemove = new Set<L.Layer>();

            featureGroup.current.eachLayer((layer) => {
                if (layer instanceof L.Polygon) {
                    const latlngs = layer.getLatLngs();
                    const coordsArrays = getCoordsFromLatLngs(latlngs);

                    if (!coordsArrays.length) {
                        return null;
                    }

                    for (const ring of coordsArrays) {
                        const firstPoint = ring[0];
                        const lastPoint = ring[ring.length - 1];
                        if (firstPoint && lastPoint && (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1])) {
                            ring.push([...firstPoint]);
                        }
                    }

                    let feat;
                    try {
                        const originalBoundary = (layer as any)._originalBoundary;
                        if (originalBoundary) {
                            feat = polygon(originalBoundary as any);
                        } else {
                            const existingFcWgs: GeoJSON.FeatureCollection = {
                                type: "FeatureCollection",
                                features: [
                                    {
                                        type: "Feature",
                                        properties: {},
                                        geometry: { type: "Polygon", coordinates: coordsArrays } as any,
                                    },
                                ],
                            };
                            const existingFc27700 = GeometryProjector.projectTo27700(existingFcWgs);
                            const projectedExistingCoords = (existingFc27700.features[0].geometry as GeoJSON.Polygon).coordinates as any;
                            feat = polygon(projectedExistingCoords);
                        }
                    } catch (error) {
                        console.error("Error creating existing polygon:", error);
                        return null;
                    }

                    try {
                        if (intersect(feat, merged)) {
                            merged = union(merged, feat);
                            overlapping.push(layer);
                            layersToRemove.add(layer);
                        }
                    } catch (error) {
                        console.error("Error processing polygon intersection:", error);
                    }
                }
            });

            try {
                const mergedGeometry = merged.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
                removePointsInsideProjectedGeometry(mergedGeometry);
            } catch (error) {
                console.error("Error preparing merged geometry for point removal:", error);
            }

            layersToRemove.forEach((layer) => {
                featureGroup.current.removeLayer(layer);
                if (mapInstance.hasLayer(layer)) {
                    mapInstance.removeLayer(layer);
                }
            });
            mapInstance.removeLayer(newLayer);

            if (!merged) {
                console.error("No valid merged polygon created");
                return null;
            }

            const merged27700 = merged.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
            const resultFc27700: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: merged27700 as any }] };
            const resultWgs = GeometryProjector.projectToWGS(resultFc27700);
            const geom = (resultWgs as any).features[0].geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
            let resultLayer: L.Polygon | null = null;

            if (geom.type === "Polygon") {
                const latlngs = geom.coordinates.map((ring: any[]) => ring.map(([lon, lat]: number[]) => [lat, lon] as [number, number]));

                resultLayer = L.polygon(latlngs, {
                    ...MapViewHelper.getLayerStyling("polygon"),
                    interactive: true,
                    fillRule: "evenodd",
                });

                (resultLayer as any)._originalBoundary = (merged27700 as any).coordinates;
                featureGroup.current.addLayer(resultLayer);
                resultLayer.on("click", (e: L.LeafletMouseEvent) => {
                    layerOnClick(e, resultLayer!);
                });
            } else if (geom.type === "MultiPolygon") {
                let firstResultLayer = null;
                geom.coordinates.forEach((polyCoords: any[], idx: number) => {
                    const latlngs = polyCoords.map((ring: any[]) => ring.map(([lon, lat]: number[]) => [lat, lon] as [number, number]));

                    const polyLayer = L.polygon(latlngs, {
                        ...MapViewHelper.getLayerStyling("polygon"),
                        interactive: true,
                        fillRule: "evenodd",
                    });

                    if (idx === 0) {
                        firstResultLayer = polyLayer;
                    }

                    (polyLayer as any)._originalBoundary = (merged27700 as any).coordinates[idx];
                    featureGroup.current.addLayer(polyLayer);
                    polyLayer.on("click", (e: L.LeafletMouseEvent) => {
                        layerOnClick(e, polyLayer);
                    });
                });
                resultLayer = firstResultLayer;
            }

            saveCurrentState();
            return resultLayer;
        },
        [map, featureGroup, saveCurrentState, getCoordsFromLatLngs, layerOnClick]
    );

    const subtractPolygons = useCallback(
        (newLayer: L.Polygon) => {
            if (!map) {
                return;
            }
            const mapInstance = map;

            const newLatLngs = newLayer.getLatLngs();
            const newCoords = getCoordsFromLatLngs(newLatLngs);

            if (newCoords.length > 0 && newCoords[0].length > 0) {
                const outerRing = newCoords[0];
                const firstPoint = outerRing[0];
                const lastPoint = outerRing[outerRing.length - 1];

                if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
                    outerRing.push([...firstPoint]);
                }

                for (let i = 1; i < newCoords.length; i++) {
                    const holeRing = newCoords[i];
                    const firstHolePoint = holeRing[0];
                    const lastHolePoint = holeRing[holeRing.length - 1];

                    if (firstHolePoint[0] !== lastHolePoint[0] || firstHolePoint[1] !== lastHolePoint[1]) {
                        holeRing.push([...firstHolePoint]);
                    }
                }
            }

            const subtractFcWgs: GeoJSON.FeatureCollection = {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        properties: {},
                        geometry: { type: "Polygon", coordinates: newCoords } as any,
                    },
                ],
            };
            const subtractFc27700 = GeometryProjector.projectTo27700(subtractFcWgs);
            const projectedSubtractCoords = (subtractFc27700.features[0].geometry as GeoJSON.Polygon).coordinates as any;
            const subtractPoly = polygon(projectedSubtractCoords);
            const modifiedLayers: L.Polygon[] = [];
            let resultGeometry: GeoJSON.Geometry = { type: "Polygon", coordinates: [] as any[] };

            // Remove any feature points that fall inside the subtract polygon
            removePointsInsideProjectedGeometry(subtractPoly.geometry as GeoJSON.Polygon);

            featureGroup.current.eachLayer((layer) => {
                if (layer instanceof L.Polygon) {
                    const latlngs = layer.getLatLngs();
                    const coords = getCoordsFromLatLngs(latlngs);

                    coords.forEach((ring) => {
                        if (ring.length > 0) {
                            const first = ring[0];
                            const last = ring[ring.length - 1];
                            if (first[0] !== last[0] || first[1] !== last[1]) {
                                ring.push([...first]);
                            }
                        }
                    });

                    const originalBoundary = (layer as any)._originalBoundary;
                    let existingPoly;
                    if (originalBoundary) {
                        existingPoly = polygon(originalBoundary as any);
                    } else {
                        const existingFcWgs: GeoJSON.FeatureCollection = {
                            type: "FeatureCollection",
                            features: [
                                {
                                    type: "Feature",
                                    properties: {},
                                    geometry: { type: "Polygon", coordinates: coords } as any,
                                },
                            ],
                        };
                        const existingFc27700 = GeometryProjector.projectTo27700(existingFcWgs);
                        const projectedExistingCoords = (existingFc27700.features[0].geometry as GeoJSON.Polygon).coordinates as any;
                        existingPoly = polygon(projectedExistingCoords);
                    }
                    if (!intersect(existingPoly, subtractPoly)) {
                        return;
                    }

                    const result = difference(existingPoly, subtractPoly);
                    mapInstance.removeLayer(layer);
                    modifiedLayers.push(layer);

                    if (!result || !result.geometry) {
                        return;
                    }

                    resultGeometry = result.geometry;

                    if (!resultGeometry) {
                        return;
                    }
                    const resFc27700: GeoJSON.FeatureCollection = {
                        type: "FeatureCollection",
                        features: [{ type: "Feature", properties: {}, geometry: resultGeometry as any }],
                    };
                    const resWgs = GeometryProjector.projectToWGS(resFc27700);
                    const geom = (resWgs as any).features[0].geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;

                    if (geom.type === "Polygon") {
                        const resultLatLngs = geom.coordinates.map((ring) => ring.map(([lon, lat]: number[]) => [lat, lon] as [number, number]));

                        const polygonLayer = L.polygon(resultLatLngs, {
                            ...MapViewHelper.getLayerStyling("polygon"),
                            interactive: true,
                            fillRule: "evenodd",
                        }).addTo(mapInstance);

                        (polygonLayer as any)._originalBoundary = (resultGeometry as any).coordinates;
                        polygonLayer.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, polygonLayer);
                        });

                        featureGroup.current.addLayer(polygonLayer);
                    } else if (geom.type === "MultiPolygon") {
                        (geom.coordinates as any[]).forEach((polyCoords, idx) => {
                            const resultLatLngs = polyCoords.map((ring: any) => ring.map(([lon, lat]: number[]) => [lat, lon] as [number, number]));

                            const polygonLayer = L.polygon(resultLatLngs, {
                                ...MapViewHelper.getLayerStyling("polygon"),
                                interactive: true,
                                fillRule: "evenodd",
                            }).addTo(mapInstance);

                            (polygonLayer as any)._originalBoundary = (resultGeometry as any).coordinates[idx];
                            polygonLayer.on("click", (e: L.LeafletMouseEvent) => {
                                layerOnClick(e, polygonLayer);
                            });

                            featureGroup.current.addLayer(polygonLayer);
                        });
                    }
                }
            });

            featureGroup.current.eachLayer((layer) => {
                if (layer instanceof L.Polygon && modifiedLayers.includes(layer)) {
                    featureGroup.current.removeLayer(layer);
                }
            });
            const newWorkingGeometry = featureGroupToWorkingGeometry(featureGroup);

            mapInstance.removeLayer(newLayer);

            if (onChange) {
                const feature27700 = { type: "Feature", properties: {}, geometry: resultGeometry } as GeoJSON.Feature;
                onChange(newWorkingGeometry, JSON.stringify(feature27700));
            }
        },
        [map, getCoordsFromLatLngs, featureGroup, featureGroupToWorkingGeometry, onChange, layerOnClick, removePointsInsideProjectedGeometry]
    );

    const ensureFeatureGroupLayersOnMap = useCallback(() => {
        if (!map) {
            return;
        }

        featureGroup.current.eachLayer((layer) => {
            if (!map.hasLayer(layer)) {
                map.addLayer(layer);
            }
        });
    }, [map, featureGroup]);

    useEffect(() => {
        if (!map) {
            return;
        }

        ensureFeatureGroupLayersOnMap();
    }, [map, ensureFeatureGroupLayersOnMap, drawModeRef]);

    const savePointsToLayers = useCallback(
        (isPolygon = false) => {
            if (!map || points.current.length < 2) {
                return;
            }

            if (singleGeometryMode && featureGroup.current.getLayers().length > 0) {
                featureGroup.current.eachLayer((layer) => {
                    if (!(layer as any)._initialBoundary) {
                        featureGroup.current.removeLayer(layer);
                        if (map.hasLayer(layer)) {
                            map.removeLayer(layer);
                        }
                    }
                });
            }

            let layer: L.Polygon | L.Polyline<any> | null = null;
            if (isPolygon) {
                if (points.current.length < 3) {
                    return;
                }

                let polygonPoints = [...points.current];
                if (polygonPoints.length > 0) {
                    const seqClean: L.LatLng[] = [];
                    for (let i = 0; i < polygonPoints.length; i++) {
                        const cur = polygonPoints[i];
                        const prev = i > 0 ? polygonPoints[i - 1] : null;
                        if (!prev || !(prev.lat === cur.lat && prev.lng === cur.lng)) {
                            seqClean.push(cur);
                        }
                    }
                    const seen = new Set<string>();
                    const dedup: L.LatLng[] = [];
                    for (const p of seqClean) {
                        const key = `${p.lat}|${p.lng}`;
                        if (!seen.has(key)) {
                            seen.add(key);
                            dedup.push(p);
                        }
                    }
                    polygonPoints = dedup;
                }
                if (polygonPoints.length < 3) {
                    return;
                }
                const firstPoint = polygonPoints[0];
                const lastPoint = polygonPoints[polygonPoints.length - 1];

                if (firstPoint.lat !== lastPoint.lat || firstPoint.lng !== lastPoint.lng) {
                    polygonPoints.push(firstPoint);
                }

                try {
                    const coords = polygonPoints.map((p) => [p.lng, p.lat]);
                    const closed =
                        coords.length > 0 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1] ? coords : [...coords, coords[0]];
                    const gj: GeoJSON.Feature<GeoJSON.Polygon> = {
                        type: "Feature",
                        properties: {},
                        geometry: { type: "Polygon", coordinates: [closed] },
                    } as any;
                    const unk: any = unkinkPolygon(gj as any);

                    if (currentLayerRef.current) {
                        if (featureGroup.current.hasLayer(currentLayerRef.current)) {
                            featureGroup.current.removeLayer(currentLayerRef.current);
                        }
                        if (map.hasLayer(currentLayerRef.current)) {
                            map.removeLayer(currentLayerRef.current);
                        }
                        currentLayerRef.current = null;
                    }

                    let firstPartLayer: L.Polygon | null = null;
                    const hasExistingPolys = featureGroup.current.getLayers().some((l) => l instanceof L.Polygon);

                    if (unk && Array.isArray(unk.features) && unk.features.length > 0) {
                        const fcWgs: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: unk.features as any } as any;
                        const fc277 = GeometryProjector.projectTo27700(fcWgs);
                        let acc: any = null;
                        for (const f of fc277.features as any) {
                            if (!acc) {
                                acc = f as any;
                            } else {
                                try {
                                    acc = symDiff(acc as any, f as any) as any;
                                } catch {
                                    acc = union(acc as any, f as any) as any;
                                }
                            }
                        }
                        const resFc277: GeoJSON.FeatureCollection =
                            acc && (acc as any).type === "FeatureCollection" ? (acc as any) : ({ type: "FeatureCollection", features: [acc as any] } as any);
                        const resWgs = GeometryProjector.projectToWGS(resFc277);

                        const addPolygonFromCoords = (polyCoords: number[][][], projectedGeom?: GeoJSON.Polygon | GeoJSON.MultiPolygon) => {
                            const latlngs = polyCoords.map((ring) => ring.map(([x, y]) => L.latLng(y, x)));
                            const partLayer = L.polygon(latlngs as any, {
                                ...MapViewHelper.getLayerStyling("polygon"),
                                interactive: true,
                                className: "accepted-polygon",
                                fillRule: "evenodd",
                            });
                            if (hasExistingPolys && !singleGeometryMode) {
                                unionPolygons(partLayer);
                            } else {
                                featureGroup.current.addLayer(partLayer);
                                map.addLayer(partLayer);
                                partLayer.on("click", (e: L.LeafletMouseEvent) => {
                                    layerOnClick(e, partLayer);
                                });
                                if (projectedGeom) {
                                    removePointsInsideProjectedGeometry(projectedGeom);
                                }
                                if (!firstPartLayer) firstPartLayer = partLayer;
                            }
                        };

                        let addedCount = 0;
                        (resWgs as any).features.forEach((feat: any, idx: number) => {
                            if (!feat || !feat.geometry) return;
                            const projectedGeom = (resFc277.features[idx]?.geometry || null) as GeoJSON.Polygon | GeoJSON.MultiPolygon;
                            if (feat.geometry.type === "Polygon") {
                                addPolygonFromCoords(feat.geometry.coordinates, projectedGeom);
                                addedCount++;
                            } else if (feat.geometry.type === "MultiPolygon") {
                                feat.geometry.coordinates.forEach((polyCoords: number[][][]) => {
                                    addPolygonFromCoords(polyCoords, projectedGeom);
                                    addedCount++;
                                });
                            }
                        });

                        if (addedCount > 0) {
                            pushHistory(
                                {
                                    points: [],
                                    featureGroup: featureGroup.current,
                                    drawMode: null,
                                    isComplete: true,
                                    isDrawing: false,
                                },
                                false
                            );
                        }
                        layer = firstPartLayer;
                    } else {
                        const single = L.polygon(polygonPoints as any, {
                            ...MapViewHelper.getLayerStyling("polygon"),
                            interactive: true,
                            className: "accepted-polygon",
                            fillRule: "evenodd",
                        });
                        if (hasExistingPolys && !singleGeometryMode) {
                            unionPolygons(single);
                        } else {
                            featureGroup.current.addLayer(single);
                            map.addLayer(single);
                            const singleFcWgs: GeoJSON.FeatureCollection = {
                                type: "FeatureCollection",
                                features: [
                                    {
                                        type: "Feature",
                                        properties: {},
                                        geometry: single.toGeoJSON().geometry as any,
                                    },
                                ],
                            };
                            try {
                                const singleProjected = GeometryProjector.projectTo27700(singleFcWgs);
                                const geom = singleProjected.features[0].geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon;
                                removePointsInsideProjectedGeometry(geom);
                            } catch (error) {
                                console.error("Error projecting polygon for point removal:", error);
                            }
                            pushHistory(
                                {
                                    points: [],
                                    featureGroup: featureGroup.current,
                                    drawMode: null,
                                    isComplete: true,
                                    isDrawing: false,
                                },
                                false
                            );
                        }
                        layer = single;
                    }
                } catch (e) {
                    if (currentLayerRef.current && currentLayerRef.current instanceof L.Polygon && !singleGeometryMode) {
                        layer = unionPolygons(currentLayerRef.current as L.Polygon);
                        if (currentLayerRef.current) {
                            currentLayerRef.current = null;
                        }
                    } else {
                        layer = L.polygon(polygonPoints, {
                            ...MapViewHelper.getLayerStyling("polygon"),
                            interactive: true,
                            className: "accepted-polygon",
                            fillRule: "evenodd",
                        });

                        if (featureGroup.current.getLayers().some((l) => l instanceof L.Polygon) && !singleGeometryMode) {
                            unionPolygons(layer);
                        } else {
                            featureGroup.current.addLayer(layer);
                            map.addLayer(layer);
                            pushHistory(
                                {
                                    points: [],
                                    featureGroup: featureGroup.current,
                                    drawMode: null,
                                    isComplete: true,
                                    isDrawing: false,
                                },
                                false
                            );
                        }
                    }
                }
            } else {
                layer = L.polyline(points.current, {
                    ...MapViewHelper.getLayerStyling("polyline"),
                    interactive: true,
                } as any) as any;

                if (layer) {
                    featureGroup.current.addLayer(layer);
                    map.addLayer(layer);
                }
                saveCurrentState();
            }
            const newWorkingGeometry = featureGroupToWorkingGeometry(featureGroup);

            ensureFeatureGroupLayersOnMap();

            points.current = [];
            if (currentLayerRef.current) {
                map.removeLayer(currentLayerRef.current);
                currentLayerRef.current = null;
            }
            if (tempLine.current) {
                map.removeLayer(tempLine.current);
                tempLine.current = null;
            }
            circleMarkers.current.forEach((marker) => map.removeLayer(marker));
            circleMarkers.current = [];
            if (firstPointCircle.current) {
                map.removeLayer(firstPointCircle.current);
                firstPointCircle.current = null;
            }
            if (onChange) {
                const resultGeometry: GeoJSON.Geometry | null = layer ? layer.toGeoJSON().geometry : null;
                const feature = { geometry: resultGeometry, type: "Feature" } as GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
                const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [feature] };
                const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                onChange(newWorkingGeometry, JSON.stringify(reprojectedGeometry.features[0]));
            }
        },
        [map, points, featureGroupToWorkingGeometry, featureGroup, ensureFeatureGroupLayersOnMap, onChange, unionPolygons, pushHistory, saveCurrentState, singleGeometryMode]
    );

    const handleFirstVertexClick = useCallback(
        (evt?: L.LeafletMouseEvent) => {
            if (evt?.originalEvent) {
                L.DomEvent.stopPropagation(evt.originalEvent);
                L.DomEvent.preventDefault(evt.originalEvent);
            }

            if (points.current.length >= 3) {
                const drawMode = drawModeRef.current;

                if (drawMode === GAIA_DRAW_MODE.POLYGON) {
                    savePointsToLayers(true);
                } else if (drawMode === GAIA_DRAW_MODE.POLYLINE) {
                    savePointsToLayers(false);
                } else if (drawMode === GAIA_DRAW_MODE.SUBTRACT && currentLayerRef.current) {
                    subtractPolygons(currentLayerRef.current as L.Polygon);
                } else if ((drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT) && onSelect) {
                    const coordinates = points.current.map((point) => [point.lng, point.lat]);
                    const geometry: GeoJSON.Geometry = {
                        type: "Polygon" as const,
                        coordinates: [[...coordinates, coordinates[0]]],
                    };
                    const geoJsonData: GeoJSON.Feature<GeoJSON.Polygon> = {
                        type: "Feature" as const,
                        properties: {},
                        geometry: geometry as GeoJSON.Polygon,
                    };
                    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [geoJsonData] };
                    const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                    onSelect(reprojectedGeometry.features[0] as GeoJSON.Feature<GeoJSON.Polygon>, drawMode === GAIA_DRAW_MODE.SELECT ? "select" : "deselect");
                }

                clearCurrentState();
                drawModeRef.current = null;
                setDrawMode(null);
            }
        },
        [points, drawModeRef, savePointsToLayers, subtractPolygons, clearCurrentState, setDrawMode, onSelect]
    );

    const styleFirstVertexMarker = useCallback(() => {
        if (!firstPointCircle.current || !map) {
            return;
        }

        const element = firstPointCircle.current.getElement();
        if (element) {
            const htmlEl = element as HTMLElement;
            htmlEl.style.cursor = "pointer";
            htmlEl.style.transformOrigin = "center";
            htmlEl.style.zIndex = "1000";
            htmlEl.style.pointerEvents = "auto";

            htmlEl.classList.add("first-vertex-marker");
        }

        firstPointCircle.current.options.interactive = true;

        firstPointCircle.current.off("click");
        firstPointCircle.current.on("click", (evt) => {
            if (evt.originalEvent) {
                L.DomEvent.stopPropagation(evt.originalEvent);
                L.DomEvent.preventDefault(evt.originalEvent);
            }

            if (points.current.length >= 3) {
                const drawMode = drawModeRef.current;
                if (drawMode === GAIA_DRAW_MODE.POLYGON) {
                    savePointsToLayers(true);
                } else if (drawMode === GAIA_DRAW_MODE.POLYLINE) {
                    savePointsToLayers(false);
                } else if (drawMode === GAIA_DRAW_MODE.SUBTRACT && currentLayerRef.current) {
                    subtractPolygons(currentLayerRef.current as L.Polygon);
                } else if ((drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT) && onSelect) {
                    const coordinates = points.current.map((point) => [point.lng, point.lat]);
                    const geometry: GeoJSON.Geometry = {
                        type: "Polygon" as const,
                        coordinates: [[...coordinates, coordinates[0]]],
                    };
                    const geoJsonData: GeoJSON.Feature<GeoJSON.Polygon> = {
                        type: "Feature" as const,
                        properties: {},
                        geometry: geometry as GeoJSON.Polygon,
                    };
                    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [geoJsonData] };
                    const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                    onSelect(reprojectedGeometry.features[0] as GeoJSON.Feature<GeoJSON.Polygon>, drawMode === GAIA_DRAW_MODE.SELECT ? "select" : "deselect");
                }

                clearCurrentState();
                drawModeRef.current = null;
                setDrawMode(null);
            }
        });
    }, [map, firstPointCircle, points, drawModeRef, clearCurrentState, savePointsToLayers, subtractPolygons, setDrawMode, onSelect]);

    const restoreState = useCallback(
        (state: MapHistoryState) => {
            if (!map) {
                return;
            }

            clearAllDrawingElements();

            featureGroup.current.eachLayer((layer) => {
                if (map.hasLayer(layer)) {
                    map.removeLayer(layer);
                }
            });

            featureGroup.current.clearLayers();

            state.featureGroup.eachLayer((layer) => {
                if (layer instanceof L.CircleMarker) {
                    const cm = L.circleMarker(layer.getLatLng(), { ...(layer as any).options } as any);
                    // preserve metadata
                    (cm as any)._addedTimestamp = (layer as any)._addedTimestamp || Date.now();
                    (cm as any)._gaiaRole = (layer as any)._gaiaRole;
                    (cm as any)._originalBoundary = (layer as any)._originalBoundary;
                    (cm as any)._initialBoundary = (layer as any)._initialBoundary;
                    featureGroup.current.addLayer(cm);
                    cm.addTo(map);
                    cm.on("click", (e) => layerOnClick(e, cm));
                } else if (layer instanceof L.Marker) {
                    const markerClone = L.marker(layer.getLatLng(), { ...layer.options });
                    // preserve metadata
                    (markerClone as any)._addedTimestamp = (layer as any)._addedTimestamp || Date.now();
                    (markerClone as any)._gaiaRole = (layer as any)._gaiaRole;
                    (markerClone as any)._originalBoundary = (layer as any)._originalBoundary;
                    (markerClone as any)._initialBoundary = (layer as any)._initialBoundary;
                    featureGroup.current.addLayer(markerClone);
                    markerClone.addTo(map);
                    markerClone.on("click", (e) => layerOnClick(e, markerClone));
                } else if ((layer as any)._isCirclePolygon) {
                    const latLngs = (layer as any).getLatLngs();
                    const clonedLayer = L.polygon(latLngs as L.LatLngExpression[], {
                        ...(layer as any).options,
                        fillRule: "evenodd",
                    });

                    if ((layer as any)._events && (layer as any)._events.click) {
                        (clonedLayer as any)._originalClickHandlers = (layer as any)._events.click;
                    }
                    if ((layer as any)._originalBoundary) {
                        (clonedLayer as any)._originalBoundary = (layer as any)._originalBoundary;
                    }

                    featureGroup.current.addLayer(clonedLayer);
                    clonedLayer.addTo(map);
                    clonedLayer.on("click", (e) => layerOnClick(e, clonedLayer));
                } else if (layer instanceof L.Polyline) {
                    const latLngs = layer.getLatLngs();
                    const newLayer =
                        layer instanceof L.Polygon
                            ? L.polygon(latLngs as L.LatLngExpression[], {
                                  ...layer.options,
                                  ...MapViewHelper.getLayerStyling("polygon"),
                                  interactive: true,
                                  fillRule: "evenodd",
                              })
                            : L.polyline(
                                  latLngs as L.LatLngExpression[],
                                  {
                                      ...layer.options,
                                      ...MapViewHelper.getLayerStyling("polyline"),
                                      interactive: true,
                                  } as any
                              );

                    if ((layer as any)._originalBoundary) {
                        (newLayer as any)._originalBoundary = (layer as any)._originalBoundary;
                    }

                    featureGroup.current.addLayer(newLayer);
                    newLayer.addTo(map);
                    newLayer.on("click", (e) => layerOnClick(e, newLayer));
                }
            });

            if (state.isDrawing && state.points.length > 0) {
                points.current = [...state.points];
                drawModeRef.current = state.drawMode;
                setDrawMode(state.drawMode);

                points.current.forEach((point, idx) => {
                    const isFirst = idx === 0;
                    const marker = L.circleMarker(point, {
                        ...MapViewHelper.getLayerStyling(isFirst ? "drawing-first-vertex-marker" : "drawing-vertex-marker"),
                        interactive: isFirst,
                        className: isFirst ? "first-vertex-marker" : "",
                        pane: "markerPane",
                        bubblingMouseEvents: false,
                    } as any).addTo(map);
                    (marker as any)._gaiaRole = "editing";

                    if (isFirst) {
                        firstPointCircle.current = marker;
                        styleFirstVertexMarker();
                    }
                    circleMarkers.current.push(marker);
                });

                if (points.current.length > 1) {
                    const isPolygon = state.drawMode === GAIA_DRAW_MODE.POLYGON || state.drawMode === GAIA_DRAW_MODE.SUBTRACT;

                    if (isPolygon) {
                        currentLayerRef.current = L.polygon([...points.current, points.current[0]], {
                            ...MapViewHelper.getLayerStyling("drawing-polygon"),
                            interactive: false,
                            className: "drawing-polygon",
                            fillRule: "evenodd",
                        }).addTo(map);
                    } else {
                        currentLayerRef.current = L.polyline(points.current, {
                            ...MapViewHelper.getLayerStyling("polyline"),
                            interactive: false,
                        } as any).addTo(map);
                    }

                    const lastPoint = points.current[points.current.length - 1];
                    const tempPoints = isPolygon ? [lastPoint, lastPoint, points.current[0]] : [lastPoint, lastPoint];
                    tempLine.current = L.polyline(tempPoints, {
                        ...MapViewHelper.getLayerStyling(isPolygon ? "drawing-polygon" : "drawing"),
                        interactive: false,
                        className: "temp-drawing",
                    } as any).addTo(map);
                }
            } else {
                points.current = [];
                drawModeRef.current = null;
                setDrawMode(null);
            }

            featureGroup.current.eachLayer((layer) => {
                if (!map.hasLayer(layer)) {
                    map.addLayer(layer);
                }
            });

            if (!state.isDrawing && onChange) {
                const mostRecentDrawnGeometry = featureGroup.current.getLayers().slice(-1)[0];
                const newWorkingGeometry = featureGroupToWorkingGeometry(featureGroup);
                let geometryString = null;
                if (mostRecentDrawnGeometry && (mostRecentDrawnGeometry as any)._originalBoundary) {
                    const feature = { geometry: (mostRecentDrawnGeometry as any)._originalBoundary, type: "Feature" } as GeoJSON.Feature<
                        GeoJSON.Geometry,
                        GeoJSON.GeoJsonProperties
                    >;
                    geometryString = JSON.stringify(feature);
                } else if (mostRecentDrawnGeometry && (mostRecentDrawnGeometry instanceof L.Polyline || mostRecentDrawnGeometry instanceof L.Polygon)) {
                    const resultGeometry = mostRecentDrawnGeometry.toGeoJSON().geometry;
                    const feature = { geometry: resultGeometry, type: "Feature" } as GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>;
                    const featureCollection: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [feature] };
                    const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                    geometryString = JSON.stringify(reprojectedGeometry.features[0]);
                }

                onChange(newWorkingGeometry, geometryString);
            }
        },
        [map, clearAllDrawingElements, featureGroup, onChange, layerOnClick, points, drawModeRef, setDrawMode, styleFirstVertexMarker, featureGroupToWorkingGeometry]
    );

    const handleCloneClick = useCallback(
        async (e: L.LeafletMouseEvent) => {
            if (!map || !onClone) {
                return;
            }

            const boundary = workingGeometry || "";

            const req: GetBoundaryRequest = { boundary };

            if (e.latlng) {
                const geoJsonPoint = { type: "Point", coordinates: [e.latlng.lng, e.latlng.lat] } as GeoJSON.Point;
                const featureCollection: GeoJSON.FeatureCollection = {
                    type: "FeatureCollection",
                    features: [{ type: "Feature", geometry: geoJsonPoint, properties: {} }],
                };
                const projected = GeometryProjector.projectTo27700(featureCollection);
                req.nextGeometry = JSON.stringify(projected.features[0].geometry);
            }

            const resp = await onClone(req);

            if (resp.errorMsg) {
                showAlert(`Error cloning boundary ${resp.errorMsg}`, "error");
                return;
            }
            featureGroup.current.eachLayer((layer) => {
                map.removeLayer(layer);
            });
            featureGroup.current.clearLayers();
            const boundaryString = resp.boundary || null;
            if (!boundaryString || boundaryString === "{}") {
                updateWorkingGeometry(boundaryString || "");
                saveCurrentState();
                if (onChange) {
                    onChange(boundaryString, boundaryString);
                }
                return;
            }
            updateWorkingGeometry(boundaryString);
            if (onChange) {
                onChange(boundaryString, boundaryString);
            }
            const newBoundary = JSON.parse(boundaryString) as GeoJSON.MultiPolygon;

            if (newBoundary.type === "MultiPolygon") {
                for (let i = 0; i < newBoundary.coordinates.length; i++) {
                    const singlePolygon: GeoJSON.Polygon = {
                        type: "Polygon",
                        coordinates: newBoundary.coordinates[i],
                    };
                    const reprojectedPolygon = GeometryProjector.projectToWGS(singlePolygon);
                    const originalPolygonBoundary = newBoundary.coordinates[i];
                    const polygonLayer = L.geoJSON(reprojectedPolygon, {
                        style: {
                            ...MapViewHelper.getLayerStyling("polygon"),
                            interactive: true,
                        } as any,
                    }).getLayers()[0] as L.Polygon;
                    (polygonLayer as any)._originalBoundary = originalPolygonBoundary;
                    featureGroup.current.addLayer(polygonLayer);
                    polygonLayer.addTo(map);

                    polygonLayer.on("click", (e: L.LeafletMouseEvent) => {
                        layerOnClick(e, polygonLayer);
                    });
                }
            } else {
                const reprojected = GeometryProjector.projectToWGS(newBoundary);
                L.geoJson(reprojected, {
                    style: {
                        ...MapViewHelper.getLayerStyling("polygon"),
                        interactive: true,
                    } as any,
                }).eachLayer((l) => {
                    if (l instanceof L.Polygon) {
                        (l as any)._originalBoundary = boundaryString;
                        featureGroup.current.addLayer(l);
                        l.addTo(map);
                        l.on("click", (e: L.LeafletMouseEvent) => {
                            layerOnClick(e, l);
                        });
                    }
                });
            }
            saveCurrentState();
        },
        [map, onClone, workingGeometry, featureGroup, updateWorkingGeometry, onChange, saveCurrentState, showAlert, layerOnClick]
    );

    const handleMouseMove = useCallback(
        (e: LeafletMouseEvent) => {
            if (!map) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.EDIT) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.LABELS) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.CIRCLE && circleCenter.current) {
                const radius = circleCenter.current.distanceTo(e.latlng);

                if (tempCircle.current) {
                    map.removeLayer(tempCircle.current);
                }
                const centerPoint = point([circleCenter.current.lng, circleCenter.current.lat]);

                const turfCircle = circle(centerPoint, radius, { units: "meters" });
                const coordinates = turfCircle.geometry.coordinates[0].map(([lng, lat]: number[]) => [lat, lng] as L.LatLngTuple);
                tempCircle.current = L.polygon(coordinates, {
                    ...MapViewHelper.getLayerStyling("drawing-circle"),
                    interactive: false,
                    className: "temp-drawing",
                    fillRule: "evenodd",
                });

                map.addLayer(tempCircle.current);

                if (circleTooltip.current) {
                    map.removeLayer(circleTooltip.current);
                }

                const formattedRadius = radius < 1000 ? `${radius.toFixed(2)} m` : `${(radius / 1000).toFixed(2)} km`;

                circleTooltip.current = L.tooltip({
                    permanent: true,
                    direction: "right",
                    className: "ruler-tooltip",
                })
                    .setLatLng(e.latlng)
                    .setContent(`Radius: ${formattedRadius}`)
                    .addTo(map);

                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.RULER && rulerPoints.current.length > 0) {
                if (rulerLine.current) {
                    map.removeLayer(rulerLine.current);
                }

                const rulerPointsList = [...rulerPoints.current, e.latlng];
                rulerLine.current = L.polyline(rulerPointsList, {
                    ...MapViewHelper.getLayerStyling("ruler-line"),
                    interactive: false,
                } as any).addTo(map);

                updateRulerTooltip(rulerPoints.current, e.latlng);
                return;
            }

            const currentDrawMode = drawModeRef.current;
            if (!currentDrawMode || points.current.length === 0) {
                return;
            }

            if (
                currentDrawMode === GAIA_DRAW_MODE.POLYLINE ||
                currentDrawMode === GAIA_DRAW_MODE.POLYGON ||
                currentDrawMode === GAIA_DRAW_MODE.SUBTRACT ||
                currentDrawMode === GAIA_DRAW_MODE.SELECT ||
                currentDrawMode === GAIA_DRAW_MODE.DESELECT
            ) {
                const lastPoint = points.current[points.current.length - 1];
                const isPolygon =
                    currentDrawMode === GAIA_DRAW_MODE.POLYGON ||
                    currentDrawMode === GAIA_DRAW_MODE.SUBTRACT ||
                    currentDrawMode === GAIA_DRAW_MODE.SELECT ||
                    currentDrawMode === GAIA_DRAW_MODE.DESELECT;
                const tempPoints = isPolygon ? [lastPoint, e.latlng, points.current[0]] : [lastPoint, e.latlng];

                const style = isPolygon ? "drawing-polygon" : "drawing";
                const options = {
                    ...MapViewHelper.getLayerStyling(style),
                    interactive: false,
                    className: "temp-drawing",
                    dashArray: "5, 10",
                };

                if (tempLine.current instanceof L.Polyline) {
                    tempLine.current.setLatLngs(tempPoints);
                    tempLine.current.setStyle(options as any);
                } else {
                    tempLine.current = L.polyline(tempPoints, options as any).addTo(map);
                }
            }
        },
        [map, drawModeRef, points, tempCircle, updateRulerTooltip]
    );

    const finishRulerAsPolygon = useCallback(() => {
        if (!map || rulerPoints.current.length < 3) {
            return;
        }

        const closedPoints = [...rulerPoints.current, rulerPoints.current[0]];

        const polygon = L.polygon(rulerPoints.current, {
            ...MapViewHelper.getLayerStyling("ruler-polygon"),
            interactive: true,
            className: "ruler-polygon",
            fillRule: "evenodd",
        });

        const rulerLineId = Date.now().toString();

        const perimeter = calculateDistance(closedPoints);
        const formattedPerimeter = perimeter < 1 ? `${(perimeter * 1000).toFixed(1)} m` : `${perimeter.toFixed(2)} km`;

        const polygonArea = calculateArea(rulerPoints.current);
        const formattedArea = polygonArea < 1 ? `${(polygonArea * 1000000).toFixed(1)} m²` : `${polygonArea.toFixed(2)} km²`;

        const tooltipContent = document.createElement("div");
        tooltipContent.style.display = "flex";
        tooltipContent.style.flexDirection = "column";
        tooltipContent.style.gap = "4px";
        tooltipContent.className = "ruler-tooltip-content";

        const perimeterText = document.createElement("div");
        perimeterText.textContent = `Perimeter: ${formattedPerimeter}`;
        tooltipContent.appendChild(perimeterText);

        const areaText = document.createElement("div");
        areaText.textContent = `Area: ${formattedArea}`;
        tooltipContent.appendChild(areaText);

        const deleteRow = document.createElement("div");
        deleteRow.style.display = "flex";
        deleteRow.style.justifyContent = "flex-end";
        deleteRow.style.marginTop = "4px";

        const deleteButton = document.createElement("button");
        deleteButton.className = "ruler-delete-button";
        deleteButton.textContent = "❌";
        deleteButton.style.background = "none";
        deleteButton.style.border = "none";
        deleteButton.style.padding = "0";
        deleteButton.style.cursor = "pointer";
        deleteButton.style.fontSize = "12px";
        deleteButton.style.lineHeight = "1";
        deleteButton.title = "Delete measurement";

        deleteRow.appendChild(deleteButton);
        tooltipContent.appendChild(deleteRow);

        const tooltip = L.tooltip({
            permanent: true,
            direction: "right",
            className: "ruler-polygon-tooltip",
            offset: [10, 0],
            interactive: true,
        })
            .setLatLng(rulerPoints.current[0])
            .setContent(tooltipContent);

        const measurement = L.featureGroup();

        measurement.addLayer(polygon);
        measurement.addLayer(tooltip);

        const segments = [...rulerLineSegments.current];

        rulerLineSegments.current = [];

        function removeRulerPolygon() {
            if (map && map.hasLayer(measurement)) {
                map.removeLayer(measurement);
            }

            segments.forEach((segment) => {
                if (map && map.hasLayer(segment)) {
                    map.removeLayer(segment);
                }
            });

            rulerLines.current = rulerLines.current.filter((rl) => rl.id !== rulerLineId);
        }

        deleteButton.addEventListener("click", (e) => {
            e.stopPropagation();
            e.preventDefault();
            removeRulerPolygon();
        });

        deleteButton.addEventListener("mousedown", (e) => {
            e.stopPropagation();
            e.preventDefault();
        });

        const newRulerLine: RulerLine = {
            id: rulerLineId,
            points: [...rulerPoints.current],
            line: polygon as any,
            markers: [],
            tooltip,
            segments,
        };

        rulerLines.current.push(newRulerLine);

        map.addLayer(measurement);

        if (rulerLine.current) {
            map.removeLayer(rulerLine.current);
            rulerLine.current = null;
        }
        if (distanceTooltip.current) {
            map.removeLayer(distanceTooltip.current);
            distanceTooltip.current = null;
        }

        circleMarkers.current.forEach((marker) => {
            if (map.hasLayer(marker)) {
                map.removeLayer(marker);
            }
        });
        circleMarkers.current = [];

        rulerPoints.current = [];

        firstRulerPointMarker.current = null;

        setDrawMode(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, calculateDistance, calculateArea, setDrawMode]);

    const onInspectClick = useCallback(
        async (e: LeafletMouseEvent) => {
            if (!getFeatureInfo || !wmsUrl || !map) {
                return;
            }

            if (currentInspectionMarker.current) {
                map.removeLayer(currentInspectionMarker.current);
                currentInspectionMarker.current = null;
            }

            const mapSize = map.getSize();
            const width = mapSize.x;
            const height = mapSize.y;

            const containerPoint = map.latLngToContainerPoint(e.latlng);
            const x = Math.round(containerPoint.x);
            const y = Math.round(containerPoint.y);

            const spaceRight = width - x;

            const tooltipWidth = 450;
            const tooltipHeight = 300;

            let direction: L.Direction;
            let horizontalOffset: number;

            if (spaceRight >= tooltipWidth + 20) {
                direction = "right";
                horizontalOffset = 20;
            } else {
                direction = "left";
                horizontalOffset = -20;
            }

            const tooltipHalfHeight = tooltipHeight / 2;

            const tooltipTop = y - tooltipHalfHeight;
            const tooltipBottom = y + tooltipHalfHeight;

            let verticalOffset = 0;

            if (tooltipTop < 10) {
                verticalOffset = 10 - tooltipTop;
            } else if (tooltipBottom > height - 10) {
                verticalOffset = height - 10 - tooltipBottom;
            }

            const offset: [number, number] = [horizontalOffset, verticalOffset];

            const geoJsonPoint = { type: "Point", coordinates: [e.latlng.lng, e.latlng.lat] } as GeoJSON.Point;
            const featureCollection: GeoJSON.FeatureCollection = {
                type: "FeatureCollection",
                features: [{ type: "Feature", geometry: geoJsonPoint, properties: {} }],
            };
            const projected = GeometryProjector.projectTo27700(featureCollection) as any;
            const point = projected.features[0].geometry.coordinates;
            const selectedLayerNames: string[] = [];
            wmsLayers.forEach((layer) => {
                if (!layer.selected || !layer.layer.nodes?.nodes) {
                    return;
                }
                selectedLayerNames.push(layer.layer.name);
            });
            if (selectedLayerNames.length === 0) {
                return;
            }
            const inspectResult = await getFeatureInfo({
                x,
                y,
                width,
                height,
                layers: selectedLayerNames,
                url: wmsUrl,
                easting: point[0],
                northing: point[1],
            });

            if (inspectResult?.featureInfo?.features?.length > 0) {
                const marker = L.marker(e.latlng, {
                    interactive: true,
                    bubblingMouseEvents: false,
                    pane: "markerPane",
                    zIndexOffset: 1000,
                    icon: createAwesomeIcon({
                        faIconDef: faCircleInfo,
                        markerColor: "blue",
                        iconColor: "white",
                    }),
                });

                currentInspectionMarker.current = marker;

                let currentPage = 0;
                const features = inspectResult.featureInfo.features;

                const createTooltipContent = (feature: any) => {
                    const tooltipContent = document.createElement("div");
                    tooltipContent.className = "inspection-tooltip-content";
                    tooltipContent.style.display = "flex";
                    tooltipContent.style.flexDirection = "column";
                    tooltipContent.style.padding = "8px";
                    tooltipContent.style.width = "450px";
                    tooltipContent.style.height = "300px";
                    tooltipContent.style.position = "relative";
                    tooltipContent.style.userSelect = "text";

                    tooltipContent.addEventListener("click", (e) => {
                        e.stopPropagation();
                    });
                    tooltipContent.addEventListener("mousedown", (e) => {
                        e.stopPropagation();
                    });
                    tooltipContent.addEventListener("mousemove", (e) => {
                        e.stopPropagation();
                    });

                    const layerTitle = document.createElement("div");
                    layerTitle.style.fontWeight = "bold";
                    layerTitle.style.marginBottom = "12px";
                    layerTitle.style.borderBottom = "1px solid #ccc";
                    layerTitle.style.paddingBottom = "8px";
                    layerTitle.style.userSelect = "text";
                    layerTitle.style.display = "flex";
                    layerTitle.style.alignItems = "center";
                    layerTitle.style.gap = "8px";

                    const matchingNode = wmsLayers.flatMap((l) => l.layer.nodes?.nodes || []).find((n) => n.name?.replace(STATMAP_PREFIX, "") === feature.tag);
                    if (matchingNode?.icon) {
                        const iconElement = document.createElement("img");
                        const iconSrc = matchingNode.icon.startsWith("data:") ? matchingNode.icon : `data:image/png;base64,${matchingNode.icon}`;
                        iconElement.src = iconSrc;
                        iconElement.style.width = "20px";
                        iconElement.style.height = "20px";
                        iconElement.style.flexShrink = "0";
                        iconElement.alt = "Layer icon";
                        layerTitle.appendChild(iconElement);
                    }

                    const titleTextElement = document.createElement("span");
                    titleTextElement.textContent = matchingNode?.title || feature.tag || "Layer Info";
                    layerTitle.appendChild(titleTextElement);

                    tooltipContent.appendChild(layerTitle);

                    const propertiesContainer = document.createElement("div");
                    propertiesContainer.style.overflowY = "auto";
                    propertiesContainer.style.flex = "1";
                    propertiesContainer.style.marginBottom = "12px";
                    propertiesContainer.style.userSelect = "text";
                    propertiesContainer.style.width = "100%";

                    propertiesContainer.addEventListener("wheel", (e) => {
                        e.stopPropagation();
                    });
                    propertiesContainer.addEventListener("mousedown", (e) => {
                        e.stopPropagation();
                    });
                    propertiesContainer.addEventListener("mousemove", (e) => {
                        e.stopPropagation();
                    });

                    const propertiesTable = document.createElement("table");
                    propertiesTable.style.width = "100%";
                    propertiesTable.style.borderCollapse = "collapse";

                    Object.entries(feature.properties).forEach(([key, value]) => {
                        const row = document.createElement("tr");
                        row.style.verticalAlign = "top";

                        const labelCell = document.createElement("td");
                        labelCell.style.padding = "4px 16px 4px 0";
                        labelCell.style.whiteSpace = "normal";
                        labelCell.style.wordBreak = "break-word";
                        labelCell.style.fontWeight = "bold";
                        labelCell.style.width = "140px";
                        labelCell.textContent = key;

                        const valueCell = document.createElement("td");
                        valueCell.style.padding = "4px 0";
                        valueCell.style.whiteSpace = "normal";
                        valueCell.style.wordBreak = "break-word";

                        const valueString = (value as string) || "";

                        const isUrl = /^https?:\/\/.+/.test(valueString);

                        if (isUrl) {
                            const link = document.createElement("a");
                            link.href = valueString;
                            link.textContent = valueString;
                            link.target = "_blank";
                            link.rel = "noopener noreferrer";
                            link.style.color = "#0066cc";
                            link.style.textDecoration = "underline";
                            link.addEventListener("click", (e) => {
                                e.stopPropagation();
                            });
                            valueCell.appendChild(link);
                        } else {
                            valueCell.textContent = valueString;
                        }

                        row.appendChild(labelCell);
                        row.appendChild(valueCell);
                        propertiesTable.appendChild(row);
                    });

                    propertiesContainer.appendChild(propertiesTable);
                    tooltipContent.appendChild(propertiesContainer);

                    if (features.length > 1) {
                        const navDiv = document.createElement("div");
                        navDiv.style.display = "grid";
                        navDiv.style.gridTemplateColumns = "1fr 0.1fr 0.1fr";
                        navDiv.style.gap = "8px";
                        navDiv.style.alignItems = "center";
                        navDiv.style.borderTop = "1px solid #ccc";
                        navDiv.style.paddingTop = "8px";
                        navDiv.style.width = "calc(100% - 16px)";
                        navDiv.style.position = "absolute";
                        navDiv.style.bottom = "8px";
                        navDiv.style.left = "8px";
                        navDiv.style.background = "white";

                        const prevButton = document.createElement("button");
                        prevButton.textContent = "←";
                        prevButton.style.padding = "4px 8px";
                        prevButton.style.cursor = "pointer";
                        prevButton.style.width = "100%";
                        prevButton.disabled = currentPage === 0;
                        prevButton.onclick = (e) => {
                            e.stopPropagation();
                            if (currentPage > 0) {
                                currentPage--;
                                updateTooltip();
                            }
                        };

                        const pageInfo = document.createElement("div");
                        pageInfo.style.textAlign = "left";
                        pageInfo.textContent = `${currentPage + 1} of ${features.length}`;

                        const nextButton = document.createElement("button");
                        nextButton.textContent = "→";
                        nextButton.style.padding = "4px 8px";
                        nextButton.style.cursor = "pointer";
                        nextButton.style.width = "100%";
                        nextButton.disabled = currentPage === features.length - 1;
                        nextButton.onclick = (e) => {
                            e.stopPropagation();
                            if (currentPage < features.length - 1) {
                                currentPage++;
                                updateTooltip();
                            }
                        };

                        prevButton.addEventListener("wheel", (e) => e.stopPropagation());
                        nextButton.addEventListener("wheel", (e) => e.stopPropagation());
                        pageInfo.addEventListener("wheel", (e) => e.stopPropagation());

                        navDiv.appendChild(pageInfo);
                        navDiv.appendChild(prevButton);
                        navDiv.appendChild(nextButton);
                        tooltipContent.appendChild(navDiv);
                    }

                    const deleteButton = document.createElement("button");
                    deleteButton.className = "ruler-delete-button";
                    deleteButton.textContent = "❌";
                    deleteButton.style.position = "absolute";
                    deleteButton.style.top = "8px";
                    deleteButton.style.right = "8px";
                    deleteButton.style.background = "none";
                    deleteButton.style.border = "none";
                    deleteButton.style.padding = "0";
                    deleteButton.style.cursor = "pointer";
                    deleteButton.style.fontSize = "12px";
                    deleteButton.style.lineHeight = "1";
                    deleteButton.title = "Close Inspection";
                    deleteButton.onclick = (e) => {
                        e.stopPropagation();
                        if (currentInspectionMarker.current) {
                            map.removeLayer(currentInspectionMarker.current);
                            currentInspectionMarker.current = null;
                        }
                    };
                    tooltipContent.appendChild(deleteButton);

                    return tooltipContent;
                };

                const updateTooltip = () => {
                    const content = createTooltipContent(features[currentPage]);
                    const tooltip = marker.getTooltip();
                    if (tooltip) {
                        tooltip.setContent(content);
                    }

                    setTimeout(() => {
                        const tooltipElement = (marker.getTooltip() as any)?._container;
                        if (tooltipElement) {
                            tooltipElement.style.setProperty("--arrow-offset", `${-verticalOffset}px`);
                        }
                    }, 0);
                };

                marker
                    .bindTooltip("", {
                        permanent: true,
                        direction: direction,
                        className: `inspection-tooltip inspection-tooltip-${direction}`,
                        offset: offset,
                        interactive: false,
                        opacity: 1,
                    })
                    .openTooltip();

                updateTooltip();

                setTimeout(() => {
                    const tooltipElement = (marker.getTooltip() as any)?._container;
                    if (tooltipElement) {
                        tooltipElement.style.setProperty("--arrow-offset", `${-verticalOffset}px`);
                    }
                }, 0);

                marker.addTo(map);
            }
        },
        [getFeatureInfo, wmsUrl, map, wmsLayers]
    );

    const onCircleClick = useCallback(
        async (e: LeafletMouseEvent, map: L.DrawMap) => {
            if (!circleCenter.current) {
                circleCenter.current = e.latlng;
                const marker = L.marker(e.latlng, {
                    interactive: false,
                }).addTo(map);
                circleCenterMarker.current = marker;
            } else {
                const drawRadius = Number(circleCenter.current.distanceTo(e.latlng).toFixed(2));
                let radius = drawRadius;
                const radiusField = <Field label="Radius (in meters)" mode="edit" contentType="NUMBER" value={radius} onChange={(e, v) => (radius = v)} />;
                if (!confirm) {
                    throw new Error("useConfirmDialog is not defined - please check provider configuration");
                }
                const radiusConfirmed = await confirm({ mutation: radiusField, header: "Confirm circle radius", onConfirmLabel: "Confirm", onRejectLabel: "Cancel" });
                if (!radiusConfirmed) {
                    return;
                }
                if (tempCircle.current) {
                    map.removeLayer(tempCircle.current);
                }
                if (circleCenterMarker.current) {
                    map.removeLayer(circleCenterMarker.current);
                }
                if (circleTooltip.current) {
                    map.removeLayer(circleTooltip.current);
                    circleTooltip.current = null;
                }
                const centerPoint = point([circleCenter.current.lng, circleCenter.current.lat]);

                const turfCircle = circle(centerPoint, radius, { units: "meters" });
                const coordinates = turfCircle.geometry.coordinates[0].map(([lng, lat]: number[]) => [lat, lng] as L.LatLngTuple);
                const polygonLayer = L.polygon(coordinates, {
                    ...MapViewHelper.getLayerStyling("polygon"),
                    interactive: true,
                } as any);
                (polygonLayer as any)._isCirclePolygon = true;

                if (singleGeometryMode && featureGroup.current.getLayers().length > 0) {
                    featureGroup.current.eachLayer((layer) => {
                        if (!(layer as any)._initialBoundary) {
                            featureGroup.current.removeLayer(layer);
                            if (map.hasLayer(layer)) {
                                map.removeLayer(layer);
                            }
                        }
                    });
                }

                let usedLayer: L.Polygon = polygonLayer;
                const hasExistingPolygons = featureGroup.current.getLayers().some((l) => l instanceof L.Polygon);
                if (hasExistingPolygons && !singleGeometryMode) {
                    const mergedLayer = unionPolygons(polygonLayer as L.Polygon);
                    if (mergedLayer) {
                        usedLayer = mergedLayer as L.Polygon;
                    }
                } else {
                    featureGroup.current.addLayer(polygonLayer);
                    polygonLayer.addTo(map);

                    try {
                        const feature = { type: "Feature", properties: {}, geometry: polygonLayer.toGeoJSON().geometry } as GeoJSON.Feature<GeoJSON.Geometry>;
                        const fc27700 = GeometryProjector.projectTo27700({ type: "FeatureCollection", features: [feature] });
                        const coords27700 = (fc27700.features[0].geometry as any).coordinates;
                        (polygonLayer as any)._originalBoundary = coords27700;
                    } catch {
                        console.error("Failed to project polygon to 27700");
                    }
                }
                usedLayer.on("click", (e: L.LeafletMouseEvent) => layerOnClick(e, usedLayer));

                circleCenter.current = null;
                tempCircle.current = null;
                const newWorkingGeometry = featureGroupToWorkingGeometry(featureGroup);
                if (onChange) {
                    const resultGeometry: GeoJSON.Geometry | null = usedLayer ? (usedLayer as any).toGeoJSON().geometry : null;
                    const feature = { geometry: resultGeometry, type: "Feature" } as any;
                    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [feature] };
                    const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                    onChange(newWorkingGeometry, JSON.stringify(reprojectedGeometry.features[0]));
                }
                setDrawMode(null);
                saveCurrentState(false);
            }
        },
        [confirm, featureGroup, featureGroupToWorkingGeometry, layerOnClick, onChange, saveCurrentState, setDrawMode, tempCircle, singleGeometryMode]
    );

    const onRulerClick = useCallback(
        (e: LeafletMouseEvent, map: L.DrawMap) => {
            const latLng = e.latlng.clone();
            rulerPoints.current.push(latLng);
            const marker = L.circleMarker(latLng, {
                ...MapViewHelper.getLayerStyling("ruler-vertex-marker"),
                interactive: false,
            } as any).addTo(map);
            (marker as any)._gaiaRole = "editing";

            if (rulerPoints.current.length === 1) {
                map.removeLayer(marker);
                circleMarkers.current.pop();
                const isFirstPoint = rulerPoints.current.length === 1;
                const firstMarker = L.circleMarker(latLng, {
                    ...MapViewHelper.getLayerStyling(isFirstPoint ? "ruler-first-vertex-marker" : "ruler-vertex-marker"),
                    interactive: isFirstPoint,
                    className: isFirstPoint ? "first-vertex-marker" : "",
                    bubblingMouseEvents: false,
                    pane: "markerPane",
                } as any).addTo(map);
                (firstMarker as any)._gaiaRole = "editing";

                firstRulerPointMarker.current = firstMarker;
                circleMarkers.current.push(firstMarker);

                const element = firstMarker.getElement();
                if (element) {
                    const htmlEl = element as HTMLElement;
                    htmlEl.style.cursor = "pointer";
                    htmlEl.style.zIndex = "9999";
                    htmlEl.style.pointerEvents = "auto";

                    htmlEl.addEventListener("click", (e) => {
                        e.stopPropagation();

                        if (rulerPoints.current.length >= 3) {
                            finishRulerAsPolygon();
                        }
                    });
                }

                firstMarker.on("click", (e) => {
                    if (rulerPoints.current.length >= 3) {
                        if (e.originalEvent) {
                            L.DomEvent.stopPropagation(e.originalEvent);
                            L.DomEvent.preventDefault(e.originalEvent);
                        }

                        finishRulerAsPolygon();
                    }
                });
            }

            circleMarkers.current.push(marker);

            if (rulerPoints.current.length > 1) {
                const start = rulerPoints.current[rulerPoints.current.length - 2];
                const end = rulerPoints.current[rulerPoints.current.length - 1];

                const segment = L.polyline([start, end], {
                    ...MapViewHelper.getLayerStyling("ruler-line"),
                    interactive: false,
                    className: "ruler-segment",
                } as any).addTo(map);

                rulerLineSegments.current.push(segment);
            }

            updateRulerTooltip(rulerPoints.current);
        },
        [finishRulerAsPolygon, updateRulerTooltip]
    );

    const handleMapClick = useCallback(
        async (e: LeafletMouseEvent) => {
            if (!map || drawModeRef.current === GAIA_DRAW_MODE.DELETE) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.EDIT) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.LABELS) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.CLONE_PREVIOUS) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.INSPECT) {
                await onInspectClick(e);
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.CIRCLE) {
                await onCircleClick(e, map);
                return;
            } else {
                circleCenter.current = null;
                if (circleTooltip.current) {
                    map.removeLayer(circleTooltip.current);
                    circleTooltip.current = null;
                }
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.CLONE) {
                handleCloneClick(e);
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.RULER) {
                onRulerClick(e, map);
                return;
            }

            const latLng = e.latlng.clone();
            const drawMode = drawModeRef.current;

            if (points.current.length === 0) {
                clearAllDrawingElements();
            }

            if (!drawMode) {
                return;
            }

            if (points.current.length === 0) {
                featureGroup.current.eachLayer((layer) => {
                    if (!map.hasLayer(layer)) {
                        map.addLayer(layer);
                    }
                });
            }

            if (drawMode === GAIA_DRAW_MODE.MARKER) {
                if (singleGeometryMode && featureGroup.current.getLayers().length > 0) {
                    featureGroup.current.eachLayer((layer) => {
                        if (!(layer as any)._initialBoundary) {
                            featureGroup.current.removeLayer(layer);
                            if (map.hasLayer(layer)) {
                                map.removeLayer(layer);
                            }
                        }
                    });
                }
                let marker: L.Marker | L.CircleMarker;

                if (pointAsCircleMarker) {
                    marker = L.circleMarker(latLng, {
                        ...MapViewHelper.getLayerStyling("polygon"),
                        fillRule: "evenodd",
                    });
                } else {
                    marker = L.marker(latLng, {
                        interactive: true,
                        bubblingMouseEvents: false,
                        pane: "markerPane",
                        zIndexOffset: 1000,
                        riseOnHover: true,
                        riseOffset: 2000,
                    });
                    if (pointCustomIcon) {
                        marker.setIcon(pointCustomIcon);
                    } else {
                        const defaultIcon = new L.Icon.Default({
                            className: "custom-marker-icon",
                        });
                        marker.setIcon(defaultIcon);
                    }
                }

                marker.on("click", (e: L.LeafletMouseEvent) => {
                    if (e.originalEvent) {
                        L.DomEvent.stopPropagation(e.originalEvent);
                        L.DomEvent.preventDefault(e.originalEvent);
                    }
                    layerOnClick(e, marker);
                });

                marker.addTo(map);
                // Mark as a real feature point so generic cleanups and undo logic preserve it
                (marker as any)._gaiaRole = "feature";
                (marker as any)._addedTimestamp = Date.now();
                featureGroup.current.addLayer(marker);
                const newWorkingGeometry = featureGroupToWorkingGeometry(featureGroup);
                if (onChange) {
                    suppressNextFit();
                    const resultGeometry: GeoJSON.Geometry | null = marker ? marker.toGeoJSON().geometry : null;
                    const feature = { geometry: resultGeometry, type: "Feature" } as any;
                    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [feature] };
                    const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                    onChange(newWorkingGeometry, JSON.stringify(reprojectedGeometry.features[0]));
                }
                saveCurrentState();
                setDrawMode(null);
                return;
            }

            points.current.push(latLng);

            const isFirstPoint = points.current.length === 1;
            const marker = L.circleMarker(latLng, {
                ...MapViewHelper.getLayerStyling(isFirstPoint ? "drawing-first-vertex-marker" : "drawing-vertex-marker"),
                interactive: isFirstPoint,
                className: isFirstPoint ? "first-vertex-marker" : "",
                bubblingMouseEvents: false,
                pane: "markerPane",
            } as any).addTo(map);
            (marker as any)._gaiaRole = "editing";

            if (isFirstPoint) {
                firstPointCircle.current = marker;
                styleFirstVertexMarker();
            }

            circleMarkers.current.push(marker);

            if (points.current.length >= 2) {
                const isPolygon =
                    drawMode === GAIA_DRAW_MODE.POLYGON || drawMode === GAIA_DRAW_MODE.SUBTRACT || drawMode === GAIA_DRAW_MODE.SELECT || drawMode === GAIA_DRAW_MODE.DESELECT;

                if (currentLayerRef.current) {
                    const latLngs = isPolygon ? [...points.current, points.current[0]] : points.current;
                    currentLayerRef.current.setLatLngs(latLngs);
                } else {
                    if (isPolygon) {
                        currentLayerRef.current = L.polygon([...points.current, points.current[0]], {
                            ...MapViewHelper.getLayerStyling("drawing-polygon"),
                            interactive: false,
                            className: "drawing-polygon temp-drawing",
                        } as any).addTo(map);
                    } else {
                        currentLayerRef.current = L.polyline(points.current, {
                            ...MapViewHelper.getLayerStyling("polyline"),
                            interactive: false,
                        } as any).addTo(map);
                    }
                }

                const lastPoint = points.current[points.current.length - 1];
                if (tempLine.current) {
                    if (isPolygon) {
                        tempLine.current.setLatLngs([lastPoint, latLng, points.current[0]]);
                    } else {
                        tempLine.current.setLatLngs([lastPoint, latLng]);
                    }
                } else {
                    const tempPoints = isPolygon ? [lastPoint, latLng, points.current[0]] : [lastPoint, latLng];
                    tempLine.current = L.polyline(tempPoints, {
                        ...MapViewHelper.getLayerStyling("drawing"),
                        interactive: false,
                        className: "temp-drawing",
                    } as any).addTo(map);
                }

                if (drawMode === GAIA_DRAW_MODE.POLYLINE && e.originalEvent.altKey) {
                    savePointsToLayers(false);
                }
            }

            saveCurrentState(true);
        },
        [
            map,
            drawModeRef,
            points,
            saveCurrentState,
            onInspectClick,
            onCircleClick,
            handleCloneClick,
            onRulerClick,
            clearAllDrawingElements,
            featureGroup,
            pointCustomIcon,
            setDrawMode,
            layerOnClick,
            styleFirstVertexMarker,
            savePointsToLayers,
            singleGeometryMode,
            featureGroupToWorkingGeometry,
            onChange,
        ]
    );

    const removeMarkersForLayer = useCallback(
        (layer: L.Polygon | L.Polyline) => {
            if (!map) {
                return;
            }
            const entry = editMarkersByLayer.current.get(layer);
            if (entry) {
                const all = [...entry.vertex, ...entry.mid];
                all.forEach((m) => {
                    if (map.hasLayer(m)) {
                        map.removeLayer(m);
                    }
                });
                editVertexMarkers.current = editVertexMarkers.current.filter((m) => !all.includes(m));
                editMidpointMarkers.current = editMidpointMarkers.current.filter((m) => !all.includes(m));
                editMarkersByLayer.current.delete(layer);
            }
        },
        [map]
    );

    const clearEditMarkers = useCallback(() => {
        if (!map) {
            return;
        }

        editMarkersByLayer.current.forEach((entry, lyr) => {
            const all = [...entry.vertex, ...entry.mid];
            all.forEach((m) => {
                if (map.hasLayer(m)) {
                    map.removeLayer(m);
                }
            });
        });
        editMarkersByLayer.current.clear();

        editVertexMarkers.current.forEach((marker) => {
            if (map.hasLayer(marker)) {
                map.removeLayer(marker);
            }
        });
        editVertexMarkers.current = [];

        editMidpointMarkers.current.forEach((marker) => {
            if (map.hasLayer(marker)) {
                map.removeLayer(marker);
            }
        });
        editMidpointMarkers.current = [];

        editingLayer.current = null;
    }, [map]);

    const updateGeometryFromVertices = useCallback(
        (layer: L.Polygon | L.Polyline, vertices: L.LatLng[]) => {
            if (!map) {
                return;
            }

            layer.setLatLngs(layer instanceof L.Polygon ? [...vertices, vertices[0]] : vertices);

            (layer as any)._hasPendingEdit = true;
        },
        [map]
    );

    const renderEditVertices = useCallback(
        (layer: L.Polygon | L.Polyline) => {
            if (!map || !layer) {
                return;
            }

            removeMarkersForLayer(layer);
            editingLayer.current = layer;

            const latLngs = layer.getLatLngs();
            let vertices: L.LatLng[] = Array.isArray(latLngs[0]) ? (latLngs[0] as L.LatLng[]) : (latLngs as L.LatLng[]);

            if (layer instanceof L.Polygon && vertices.length > 1) {
                const first = vertices[0];
                const last = vertices[vertices.length - 1];
                const isSamePoint = !!first && !!last && first.lat === last.lat && first.lng === last.lng;
                if (isSamePoint) {
                    vertices = vertices.slice(0, -1);
                }
            }

            if (vertices.length > 1) {
                const cleaned: L.LatLng[] = [];
                for (let i = 0; i < vertices.length; i++) {
                    const prev = i > 0 ? vertices[i - 1] : null;
                    const cur = vertices[i];
                    if (!prev || !(prev.lat === cur.lat && prev.lng === cur.lng)) {
                        cleaned.push(cur);
                    }
                }
                vertices = cleaned;
            }

            const layerVertexMarkers: L.CircleMarker[] = [];
            const layerMidMarkers: L.CircleMarker[] = [];

            const mainPaneName = "gaia-edit-main";
            const midPaneName = "gaia-edit-mid";
            if (!map.getPane(mainPaneName)) {
                const p = map.createPane(mainPaneName);
                p.style.zIndex = "650";
            }
            if (!map.getPane(midPaneName)) {
                const p = map.createPane(midPaneName);
                p.style.zIndex = "640";
            }

            const keyFor = (ll: L.LatLng) => `${ll.lat}|${ll.lng}`;
            const groups = new Map<string, number[]>();
            for (let i = 0; i < vertices.length; i++) {
                const k = keyFor(vertices[i]);
                const arr = groups.get(k);
                if (arr) {
                    arr.push(i);
                } else {
                    groups.set(k, [i]);
                }
            }
            const createdMainKeys = new Set<string>();

            vertices.forEach((vertex, index) => {
                const key = keyFor(vertex);
                let mainMarker: L.CircleMarker | null = null;

                if (!createdMainKeys.has(key)) {
                    mainMarker = L.circleMarker(vertex, {
                        radius: 8,
                        color: "#fff",
                        fillColor: "#ff0000",
                        fillOpacity: 1,
                        weight: 3,
                        interactive: true,
                        className: "edit-vertex-main",
                        pane: mainPaneName,
                        bubblingMouseEvents: false,
                    }).addTo(map);
                    (mainMarker as any)._gaiaRole = "editing";
                    mainMarker.bringToFront();

                    createdMainKeys.add(key);

                    let isDragging = false;
                    const groupIndices = groups.get(key) || [index];

                    mainMarker.on("mousedown", (e: L.LeafletMouseEvent) => {
                        L.DomEvent.stopPropagation(e.originalEvent);
                        L.DomEvent.preventDefault(e.originalEvent);
                        isDragging = true;
                        draggedVertexIndex.current = index;
                        map.dragging.disable();

                        const onMouseMove = (moveEvent: L.LeafletMouseEvent) => {
                            if (!isDragging) {
                                return;
                            }

                            const newLatLngs = [...vertices];
                            groupIndices.forEach((gi) => (newLatLngs[gi] = moveEvent.latlng));
                            mainMarker!.setLatLng(moveEvent.latlng);

                            layer.setLatLngs(layer instanceof L.Polygon ? [...newLatLngs, newLatLngs[0]] : newLatLngs);

                            renderEditVertices(layer);
                        };

                        const onMouseUp = () => {
                            if (isDragging) {
                                const newLatLngs = [...vertices];
                                const finalPos = mainMarker!.getLatLng();
                                groupIndices.forEach((gi) => (newLatLngs[gi] = finalPos));
                                updateGeometryFromVertices(layer, newLatLngs);
                                let didSplit = false;
                                if (layer instanceof L.Polygon) {
                                    didSplit = splitPolygonIfSelfIntersecting(layer as L.Polygon);
                                }
                                if (!didSplit) {
                                    renderEditVertices(layer);
                                }
                            }
                            isDragging = false;
                            draggedVertexIndex.current = null;
                            map.dragging.enable();
                            map.off("mousemove", onMouseMove);
                            map.off("mouseup", onMouseUp);
                        };

                        map.on("mousemove", onMouseMove);
                        map.on("mouseup", onMouseUp);
                    });

                    mainMarker.on("contextmenu", (e: L.LeafletMouseEvent) => {
                        L.DomEvent.stopPropagation(e.originalEvent);
                        L.DomEvent.preventDefault(e.originalEvent);

                        if (vertices.length <= 3 && layer instanceof L.Polygon) {
                            showAlert("Cannot remove vertex: polygon must have at least 3 vertices", "warning");
                            return;
                        }

                        if (vertices.length <= 2 && layer instanceof L.Polyline) {
                            showAlert("Cannot remove vertex: polyline must have at least 2 vertices", "warning");
                            return;
                        }

                        const newLatLngs = vertices.filter((_, i) => i !== index);
                        updateGeometryFromVertices(layer, newLatLngs);
                        renderEditVertices(layer);
                    });

                    editVertexMarkers.current.push(mainMarker);
                    layerVertexMarkers.push(mainMarker);
                }

                if (index < vertices.length - 1 || layer instanceof L.Polygon) {
                    const nextVertex = index < vertices.length - 1 ? vertices[index + 1] : vertices[0];
                    const midpoint = L.latLng((vertex.lat + nextVertex.lat) / 2, (vertex.lng + nextVertex.lng) / 2);

                    let shouldCreateMidpoint = true;

                    if (vertex.lat === nextVertex.lat && vertex.lng === nextVertex.lng) {
                        shouldCreateMidpoint = false;
                    }

                    const midKey = `${midpoint.lat}|${midpoint.lng}`;
                    if (midKey === keyFor(vertex) || midKey === keyFor(nextVertex)) {
                        shouldCreateMidpoint = false;
                    }

                    if (shouldCreateMidpoint) {
                        const midMarker = L.circleMarker(midpoint, {
                            radius: 5,
                            color: "#fff",
                            fillColor: "#999",
                            fillOpacity: 0.8,
                            weight: 2,
                            interactive: true,
                            className: "edit-vertex-mid",
                            pane: midPaneName,
                            bubblingMouseEvents: false,
                        }).addTo(map);
                        (midMarker as any)._gaiaRole = "editing";
                        midMarker.bringToBack();

                        let isDraggingMid = false;
                        midMarker.on("mousedown", (e: L.LeafletMouseEvent) => {
                            L.DomEvent.stopPropagation(e.originalEvent);
                            L.DomEvent.preventDefault(e.originalEvent);

                            isDraggingMid = true;
                            const insertAt = index + 1;
                            const newLatLngs = [...vertices.slice(0, insertAt), midpoint, ...vertices.slice(insertAt)];
                            draggedVertexIndex.current = insertAt;
                            map.dragging.disable();

                            if (layer instanceof L.Polygon) {
                                layer.setLatLngs([...newLatLngs, newLatLngs[0]]);
                            } else {
                                layer.setLatLngs(newLatLngs);
                            }
                            renderEditVertices(layer);

                            let insertedMarkerRef: L.CircleMarker | null = null;
                            let prevMidMarkerRef: L.CircleMarker | null = null;
                            let nextMidMarkerRef: L.CircleMarker | null = null;
                            const entry = editMarkersByLayer.current.get(layer);
                            if (entry) {
                                insertedMarkerRef =
                                    entry.vertex.find((m) => {
                                        const ll = m.getLatLng();
                                        return ll.lat === midpoint.lat && ll.lng === midpoint.lng;
                                    }) || null;

                                if (layer instanceof L.Polygon) {
                                    const prevIndex = (insertAt - 1 + newLatLngs.length) % newLatLngs.length;
                                    const nextIndex = (insertAt + 1) % newLatLngs.length;
                                    const prevMidInit = L.latLng(
                                        (newLatLngs[prevIndex].lat + newLatLngs[insertAt].lat) / 2,
                                        (newLatLngs[prevIndex].lng + newLatLngs[insertAt].lng) / 2
                                    );
                                    const nextMidInit = L.latLng(
                                        (newLatLngs[insertAt].lat + newLatLngs[nextIndex].lat) / 2,
                                        (newLatLngs[insertAt].lng + newLatLngs[nextIndex].lng) / 2
                                    );
                                    prevMidMarkerRef =
                                        entry.mid.find((m) => {
                                            const ll = m.getLatLng();
                                            return ll.lat === prevMidInit.lat && ll.lng === prevMidInit.lng;
                                        }) || null;
                                    nextMidMarkerRef =
                                        entry.mid.find((m) => {
                                            const ll = m.getLatLng();
                                            return ll.lat === nextMidInit.lat && ll.lng === nextMidInit.lng;
                                        }) || null;
                                }
                            }

                            const onMouseMove = (moveEvent: L.LeafletMouseEvent) => {
                                if (!isDraggingMid) return;
                                newLatLngs[insertAt] = moveEvent.latlng;

                                if (insertedMarkerRef) {
                                    insertedMarkerRef.setLatLng(moveEvent.latlng);
                                }

                                if (layer instanceof L.Polygon) {
                                    const prevIndex = (insertAt - 1 + newLatLngs.length) % newLatLngs.length;
                                    const nextIndex = (insertAt + 1) % newLatLngs.length;
                                    const prevMid = L.latLng(
                                        (newLatLngs[prevIndex].lat + newLatLngs[insertAt].lat) / 2,
                                        (newLatLngs[prevIndex].lng + newLatLngs[insertAt].lng) / 2
                                    );
                                    const nextMid = L.latLng(
                                        (newLatLngs[insertAt].lat + newLatLngs[nextIndex].lat) / 2,
                                        (newLatLngs[insertAt].lng + newLatLngs[nextIndex].lng) / 2
                                    );
                                    prevMidMarkerRef?.setLatLng(prevMid);
                                    nextMidMarkerRef?.setLatLng(nextMid);
                                }

                                if (layer instanceof L.Polygon) {
                                    layer.setLatLngs([...newLatLngs, newLatLngs[0]]);
                                } else {
                                    layer.setLatLngs(newLatLngs);
                                }
                            };

                            const onMouseUp = () => {
                                if (isDraggingMid) {
                                    updateGeometryFromVertices(layer, newLatLngs);
                                    let didSplit = false;
                                    if (layer instanceof L.Polygon) {
                                        didSplit = splitPolygonIfSelfIntersecting(layer as L.Polygon);
                                    }
                                    if (!didSplit) {
                                        renderEditVertices(layer);
                                    }
                                }
                                isDraggingMid = false;
                                draggedVertexIndex.current = null;
                                map.dragging.enable();
                                map.off("mousemove", onMouseMove);
                                map.off("mouseup", onMouseUp);
                            };

                            map.on("mousemove", onMouseMove);
                            map.on("mouseup", onMouseUp);
                        });

                        midMarker.on("click", (e: L.LeafletMouseEvent) => {
                            if (isDraggingMid) {
                                return;
                            }
                            L.DomEvent.stopPropagation(e.originalEvent);
                            L.DomEvent.preventDefault(e.originalEvent);

                            const newLatLngs = [...vertices.slice(0, index + 1), midpoint, ...vertices.slice(index + 1)];
                            updateGeometryFromVertices(layer, newLatLngs);
                            let didSplit = false;
                            if (layer instanceof L.Polygon) {
                                didSplit = splitPolygonIfSelfIntersecting(layer as L.Polygon);
                            }
                            if (!didSplit) {
                                renderEditVertices(layer);
                            }
                        });

                        editMidpointMarkers.current.push(midMarker);
                        layerMidMarkers.push(midMarker);
                    }

                    if (index === vertices.length - 1) {
                        editMarkersByLayer.current.set(layer, { vertex: layerVertexMarkers, mid: layerMidMarkers });
                    }
                }
            });
        },
        [map, removeMarkersForLayer, updateGeometryFromVertices, showAlert]
    );

    const splitPolygonIfSelfIntersecting = useCallback(
        (layer: L.Polygon): boolean => {
            if (!map) {
                return false;
            }
            try {
                const latLngsAny: any = layer.getLatLngs();
                const vertices: L.LatLng[] = Array.isArray(latLngsAny[0]) ? (latLngsAny[0] as L.LatLng[]) : (latLngsAny as L.LatLng[]);
                if (!vertices || vertices.length < 3) {
                    return false;
                }

                const cleaned: L.LatLng[] = [];
                for (let i = 0; i < vertices.length; i++) {
                    const prev = i > 0 ? vertices[i - 1] : null;
                    const cur = vertices[i];
                    if (!prev || !(prev.lat === cur.lat && prev.lng === cur.lng)) {
                        cleaned.push(cur);
                    }
                }
                const uniqueKeys = new Set(cleaned.map((v) => `${v.lat}|${v.lng}`));
                if (uniqueKeys.size < 3) {
                    return false;
                }

                const coords = cleaned.map((v) => [v.lng, v.lat]);
                const ring = coords.length > 0 && coords[0][0] === coords[coords.length - 1][0] && coords[0][1] === coords[coords.length - 1][1] ? coords : [...coords, coords[0]];
                const poly: GeoJSON.Feature<GeoJSON.Polygon> = {
                    type: "Feature",
                    properties: {},
                    geometry: { type: "Polygon", coordinates: [ring] },
                } as any;

                let unk: any;
                try {
                    unk = unkinkPolygon(poly as any);
                } catch (e) {
                    const seen = new Set<string>();
                    const reuniq = cleaned.filter((v) => {
                        const k = `${v.lat}|${v.lng}`;
                        if (seen.has(k)) return false;
                        seen.add(k);
                        return true;
                    });
                    if (reuniq.length < 3) return false;
                    const coords2 = reuniq.map((v) => [v.lng, v.lat]);
                    const ring2 =
                        coords2.length > 0 && coords2[0][0] === coords2[coords2.length - 1][0] && coords2[0][1] === coords2[coords2.length - 1][1]
                            ? coords2
                            : [...coords2, coords2[0]];
                    const poly2: any = { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [ring2] } };
                    try {
                        unk = unkinkPolygon(poly2);
                    } catch {
                        return false;
                    }
                }
                if (!unk || !unk.features || unk.features.length <= 1) {
                    return false;
                }

                removeMarkersForLayer(layer);
                featureGroup.current.removeLayer(layer);
                if ((map as any).hasLayer && (map as any).hasLayer(layer)) {
                    map.removeLayer(layer);
                }

                unk.features.forEach((feat: any) => {
                    if (!feat || !feat.geometry || feat.geometry.type !== "Polygon") {
                        return;
                    }
                    const c = feat.geometry.coordinates[0] || [];
                    const vtx = c.map((p: any) => L.latLng(p[1] as number, p[0] as number));
                    const newLayer = L.polygon(vtx as any, { ...MapViewHelper.getLayerStyling("polygon") } as any);
                    featureGroup.current.addLayer(newLayer);
                    newLayer.addTo(map);
                    newLayer.on("click", (e: L.LeafletMouseEvent) => {
                        layerOnClick(e, newLayer);
                    });
                    (newLayer as any)._hasPendingEdit = true;
                });

                clearEditMarkers();
                featureGroup.current.eachLayer((l: any) => {
                    if (l instanceof L.Polygon || l instanceof L.Polyline) {
                        renderEditVertices(l);
                    }
                });

                return true;
            } catch (err) {
                console.warn("Failed to split self-intersecting polygon:", err);
                return false;
            }
        },
        [map, featureGroup, removeMarkersForLayer, layerOnClick, clearEditMarkers, renderEditVertices]
    );

    const activateEditMode = useCallback(() => {
        if (!map) {
            return;
        }

        clearEditMarkers();

        const layers = featureGroup.current.getLayers();
        if (layers.length === 0) {
            return;
        }

        try {
            const snapshot: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] as any } as any;
            featureGroup.current.eachLayer((l: any) => {
                if (l instanceof L.Polygon || l instanceof L.Polyline) {
                    try {
                        const feat = (l as any).toGeoJSON();
                        if (feat) {
                            (snapshot.features as any).push(feat);
                        }
                    } catch {
                        console.warn("Failed to convert layer to GeoJSON:", l);
                    }
                }
            });
            (featureGroup.current as any)._editSessionSnapshot = snapshot;
        } catch (e) {
            console.warn("Failed to capture edit-session snapshot:", e);
        }

        layers.forEach((layer) => {
            if (layer instanceof L.Polygon || layer instanceof L.Polyline) {
                renderEditVertices(layer);
            }
        });
    }, [map, clearEditMarkers, renderEditVertices, featureGroup]);

    const deactivateEditMode = useCallback(() => {
        clearEditMarkers();
    }, [clearEditMarkers]);

    useEffect(() => {
        if (!map) {
            return;
        }

        if (drawMode === GAIA_DRAW_MODE.EDIT) {
            activateEditMode();
        } else {
            deactivateEditMode();
        }

        return () => {
            deactivateEditMode();
        };
    }, [map, drawMode, activateEditMode, deactivateEditMode]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "z") {
                try {
                    if (e.shiftKey) {
                        const redoState = getRedoState();
                        if (redoState) {
                            restoreState(redoState);
                        }
                    } else {
                        const undoState = getUndoState();
                        if (undoState) {
                            restoreState(undoState);
                        }
                    }
                } catch (error) {
                    console.error("Error during undo/redo operation:", error);
                }

                e.preventDefault();
            }

            if (e.key === "Escape" && points.current.length > 0) {
                clearCurrentState();
                setDrawMode(null);
            }

            if (e.key === "Escape" && drawModeRef.current === GAIA_DRAW_MODE.RULER) {
                rulerPoints.current = [];

                if (rulerLine.current) {
                    map.removeLayer(rulerLine.current);
                    rulerLine.current = null;
                }

                if (distanceTooltip.current) {
                    map.removeLayer(distanceTooltip.current);
                    distanceTooltip.current = null;
                }

                rulerLineSegments.current.forEach((segment) => {
                    if (map.hasLayer(segment)) {
                        map.removeLayer(segment);
                    }
                });
                rulerLineSegments.current = [];

                circleMarkers.current.forEach((marker) => {
                    if (map.hasLayer(marker)) {
                        map.removeLayer(marker);
                    }
                });
                circleMarkers.current = [];

                firstRulerPointMarker.current = null;

                setDrawMode(null);
            }
        };

        map.on("click", handleMapClick);
        map.on("mousemove", handleMouseMove);
        document.addEventListener("keydown", handleKeyDown);
        map.on("dblclick", (e: L.LeafletMouseEvent) => {
            if (!drawModeRef.current) {
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.LABELS) {
                return;
            }

            if (e.originalEvent) {
                L.DomEvent.stopPropagation(e.originalEvent);
                L.DomEvent.preventDefault(e.originalEvent);
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.RULER && rulerPoints.current.length >= 2) {
                finishRulerLine();
                return;
            }

            if (drawModeRef.current === GAIA_DRAW_MODE.POLYLINE) {
                savePointsToLayers(false);
            } else if (drawModeRef.current === GAIA_DRAW_MODE.POLYGON && points.current.length >= 3) {
                savePointsToLayers(true);
            } else if (drawModeRef.current === GAIA_DRAW_MODE.SUBTRACT && points.current.length >= 3) {
                if (currentLayerRef.current) {
                    subtractPolygons(currentLayerRef.current as L.Polygon);
                    currentLayerRef.current = null;
                    clearCurrentState();
                }
            } else if ((drawModeRef.current === GAIA_DRAW_MODE.SELECT || drawModeRef.current === GAIA_DRAW_MODE.DESELECT) && points.current.length >= 3) {
                const coordinates = points.current.map((point) => [point.lng, point.lat]);
                const geometry: GeoJSON.Geometry = {
                    type: "Polygon" as const,
                    coordinates: [[...coordinates, coordinates[0]]],
                };
                const geoJsonData: GeoJSON.Feature<GeoJSON.Polygon> = {
                    type: "Feature" as const,
                    properties: {},
                    geometry: geometry as GeoJSON.Polygon,
                };
                const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = { type: "FeatureCollection", features: [geoJsonData] };
                const reprojectedGeometry = GeometryProjector.projectTo27700(featureCollection);
                if (onSelect) {
                    onSelect(reprojectedGeometry.features[0] as GeoJSON.Feature<GeoJSON.Polygon>, drawModeRef.current === GAIA_DRAW_MODE.SELECT ? "select" : "deselect");
                }
                clearCurrentState();
            }

            drawModeRef.current = null;
            setDrawMode(null);
        });

        return () => {
            map.off("click", handleMapClick);
            map.off("mousemove", handleMouseMove);
            map.off("dblclick");
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        map,
        handleMapClick,
        handleMouseMove,
        clearCurrentState,
        getUndoState,
        getRedoState,
        restoreState,
        setDrawMode,
        points,
        drawModeRef,
        savePointsToLayers,
        subtractPolygons,
        unionPolygons,
        styleFirstVertexMarker,
        handleFirstVertexClick,
        handleCloneClick,
        updateRulerTooltip,
        finishRulerLine,
        onSelect,
        singleGeometryMode,
    ]);

    useEffect(() => {
        if (!map) {
            return;
        }

        clearAllDrawingElements();

        if (drawModeRef.current !== null) {
            points.current = [];
            if (currentLayerRef.current && !featureGroup.current.hasLayer(currentLayerRef.current)) {
                map.removeLayer(currentLayerRef.current);
                currentLayerRef.current = null;
            }
            if (tempLine.current) {
                map.removeLayer(tempLine.current);
                tempLine.current = null;
            }
        }

        if (drawModeRef.current === GAIA_DRAW_MODE.POLYGON || drawModeRef.current === GAIA_DRAW_MODE.POLYLINE || drawModeRef.current === GAIA_DRAW_MODE.SUBTRACT) {
            map.getContainer().style.cursor = "crosshair";
        } else {
            map.getContainer().style.cursor = "default";
        }
    }, [map, drawModeRef, clearAllDrawingElements, points, featureGroup]);
};
